<template>
  <!-- Welcome Section for Doctor -->
  <div class="mb-8">
    <div class="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white mb-6">
      <div v-if="doctorInfo" class="flex items-center">
        <!-- Profile photo if available -->
        <div v-if="doctorInfo.user && doctorInfo.user.face_photo" 
             class="w-16 h-16 rounded-full overflow-hidden border-2 border-white border-opacity-30 mr-4">
          <img :src="doctorInfo.user.face_photo" 
               :alt="formatDoctorName()" 
               class="w-full h-full object-cover"
               @error="onImageError">
        </div>
        <!-- Default avatar if no photo -->
        <div v-else class="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
          <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold mb-1">
            {{ $t('doctor.welcome') }}, {{ formatDoctorName() }}!
          </h1>
          <p class="text-blue-100 text-sm">{{ formatDoctorSpecialization(doctorInfo) || $t('doctor.doctors') }}</p>
          <div v-if="doctorInfo.city || doctorInfo.clinic" class="mt-2">
            <p class="text-blue-100 text-sm">
              <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              {{ formatDoctorLocation() }}
            </p>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center">
        <HeartbeatLoader />
        <div class="flex-1 ml-4">
          <div class="h-6 bg-white bg-opacity-20 rounded mb-2" style="width: 60%;"></div>
          <div class="h-4 bg-white bg-opacity-20 rounded" style="width: 40%;"></div>
        </div>
      </div>
    </div>
    
    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">

      
      <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">{{ $t('statistics.totalPatients') }}</p>
            <p class="text-2xl font-bold text-gray-900">{{ clinicPatients.length }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">
              {{ $t('statistics.todayAppointments') }}
            </p>
            <p class="text-2xl font-bold text-gray-900">{{ getTodayAppointmentsCount() }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">
              {{ $t('appointment.appointments') }}
            </p>
            <p class="text-2xl font-bold text-gray-900">{{ getUpcomingAppointmentsCount() }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div class="flex items-center">
          <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">
              {{ $t('statistics.completedAppointments') }}
            </p>
            <p class="text-2xl font-bold text-gray-900">{{ getCompletedAppointmentsCount() }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Секция с предстоящими приемами (только для врачей) -->
  <div v-if="userType === 'doctor'" class="bg-white shadow-sm rounded-xl p-6 mb-6 border border-gray-200">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold text-gray-800">{{ $t('appointment.appointments') }}</h2>
      
      <div class="flex items-center space-x-4">
        <!-- Кнопки вкладок -->
        <div class="flex space-x-2">
          <button 
            @click="activeAppointmentsTab = 'upcoming'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="activeAppointmentsTab === 'upcoming' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            {{ $t('appointment.upcomingAppointments') }}
          </button>
          <button 
            @click="activeAppointmentsTab = 'today'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="activeAppointmentsTab === 'today' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            {{ $t('common.today') }}
          </button>
          <button 
            @click="activeAppointmentsTab = 'past'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            :class="activeAppointmentsTab === 'past' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            {{ $t('appointment.pastAppointments') }}
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="appointmentsLoading" class="flex justify-center p-6">
      <HeartbeatLoader :text="$t('common.loading')" />
    </div>
    
    <div v-else-if="appointmentsError" class="p-4 bg-red-50 text-red-600 rounded-md">
      {{ appointmentsError }}
    </div>
    
    <div v-else>
      <div v-if="appointments.length > 0" class="overflow-x-auto">
        <table class="min-w-full bg-white">
          <thead>
            <tr class="bg-gray-50 text-gray-700">
              <th class="py-3 px-4 text-left font-medium">{{ $t('patientDetails.appointmentType') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('patientDetails.dateTime') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('patientDetails.visitReason') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('appointment.status') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="appointment in appointments" :key="appointment.id" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td class="py-3 px-4">
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center mr-2 bg-blue-100">
                    <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="font-medium">{{ getPatientName(appointment) }}</div>
                    <div class="text-xs text-gray-500">{{ $t('patientDetails.doctorAppointment') }}</div>
                  </div>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="text-sm">
                  <div class="font-medium">{{ formatDate(getSlotDate(appointment)) }}</div>
                  <div class="text-gray-500">{{ formatTime(getSlotStartTime(appointment)) }} - {{ formatTime(getSlotEndTime(appointment)) }}</div>
                </div>
              </td>
              <td class="py-3 px-4">
                <div>{{ getVisitReasonText(appointment.reason) || $t('patientDetails.noReasonSpecified') }}</div>
                <div v-if="appointment.is_consilium" class="mt-1">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {{ $t('consilium.consilium') }}
                  </span>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="flex items-center space-x-2">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getDisplayStatusClass(appointment.status)">
                    {{ getDisplayStatusText(appointment.status) }}
                  </span>
                  <!-- Refresh button for no-show statuses -->
                  <button 
                    v-if="appointment.status === 'no_show' || appointment.status === 'patient_no_show'"
                    @click="checkVideoAndUpdateStatus(appointment)"
                    class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                    :title="'Обновить статус'"
                    :disabled="checkingVideoStatus[appointment.id]"
                  >
                    <svg 
                      class="w-4 h-4" 
                      :class="{'animate-spin': checkingVideoStatus[appointment.id]}"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                  </button>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="flex space-x-2">
                  <button 
                    v-if="canStartConsultation(appointment)"
                    class="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    @click="startConsultation(appointment)"
                  >
                    {{ $t('appointment.startAppointment') }}
                  </button>
                  <button 
                    v-if="appointment.status === 'scheduled' || appointment.status === 'in_progress'"
                    class="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                    @click="openConsiliumDialog(appointment)"
                    :title="$t('consilium.inviteTooltip')"
                  >
                    {{ $t('consilium.invite') }}
                  </button>
                  <button 
                    v-if="appointment.status === 'in_progress' && getVideoRoomUrl(appointment)"
                    @click="joinVideoConsultation(getVideoRoomUrl(appointment), appointment)"
                    class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {{ $t('appointment.joinVideoCall') }}
                  </button>
                  <button 
                    v-if="canCompleteAppointment(appointment)"
                    class="px-3 py-1 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    @click="completeConsultation(appointment)"
                  >
                    {{ $t('appointment.completeAppointment') }}
                  </button>

                  <button 
                    v-if="appointment.status === 'completed' && hasAppointmentMedia(appointment)"
                    class="w-8 h-8 flex items-center justify-center bg-white-600 text-white rounded-md hover:bg-white-700 transition-colors"
                    @click="showRoomRecords(appointment)"
                    :title="$t('patientDetails.viewRecords')"
                  >
                    <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                    </svg>
                  </button>
                  <button 
                    v-if="appointment.status === 'completed' || appointment.status === 'patient_no_show' || appointment.status === 'no_show'"
                    class="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
                    @click="showAppointmentConclusion(appointment)"
                    :title="'Просмотр заключения'"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </button>
                  <button 
                    v-if="canCancelAppointment(appointment)"
                    class="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    @click="confirmCancelAppointment(appointment)"
                  >
                    {{ $t('common.cancel') }}
                  </button>
                  <button 
                    v-if="canLeaveConsilium(appointment)"
                    class="px-3 py-1 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    @click="confirmLeaveConsilium(appointment)"
                    :title="$t('consilium.leaveConsiliumTooltip')"
                  >
                    {{ $t('consilium.leaveConsilium') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div v-else class="text-center p-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
        <p v-if="activeAppointmentsTab === 'upcoming'">{{ $t('messages.noUpcomingAppointments') }}</p>
        <p v-else-if="activeAppointmentsTab === 'today'">{{ $t('messages.noTodayAppointments') }}</p>
        <p v-else>{{ $t('messages.noPastAppointments') }}</p>
      </div>
    </div>
  </div>

  <!-- Секция с пациентами участка врача -->
  <div v-if="showClinicPatientsSection" class="bg-white shadow-sm rounded-xl p-6 mb-6 border border-gray-200">
    <div class="flex justify-between items-center mb-4">
              <h2 class="text-xl font-semibold text-gray-800">{{ $t('doctor.clinicPatients') }}</h2>
      <div class="flex items-center space-x-4">
        <!-- Search bar -->
        <div class="relative">
          <input 
            v-model="patientsSearchQuery"
            type="text" 
            :placeholder="$t('common.search') + '...'"
            class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
          <svg class="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <!-- Nurse calls history button -->
        <button 
          v-if="userType === 'nurse'"
          @click="goToCallsHistory"
          class="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
        >
          {{ $t('navigation.callsHistory') }}
        </button>
        <!-- View all button -->
        <button 
          @click="goToPatients"
          class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          {{ $t('common.viewAll') }}
        </button>
      </div>
    </div>
    
    <div v-if="patientsLoading" class="flex justify-center p-6">
      <HeartbeatLoader :text="$t('common.loading')" />
    </div>
    
    <div v-else-if="patientsError" class="p-4 bg-red-50 text-red-600 rounded-md">
      {{ patientsError }}
    </div>
    
    <div v-else>
              <div v-if="filteredClinicPatients.length > 0" class="overflow-x-auto">
        <table class="min-w-full bg-white">
          <thead>
            <tr class="bg-gray-50 text-gray-700">
              <th class="py-3 px-4 text-left font-medium">{{ $t('doctor.personalInfo') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('common.dateOfBirth') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('common.age') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('common.iin') }}</th>
              <th class="py-3 px-4 text-left font-medium">{{ $t('common.gender') }}</th>
              <th v-if="userType === 'nurse'" class="py-3 px-4 text-left font-medium">{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="patient in filteredClinicPatients.slice(0, 5)" :key="patient.id" 
                @click="goToPatientDetails(patient)"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer">
              <td class="py-3 px-4 text-gray-900">{{ getPatientFullName(patient) }}</td>
              <td class="py-3 px-4 text-gray-600">{{ formatDate(patient.birth_date) }}</td>
              <td class="py-3 px-4 text-gray-600">
                <span class="font-medium">{{ calculateAge(patient.birth_date) }}</span>
                <span class="text-xs text-gray-500 ml-1">{{ $t('time.years') }}</span>
              </td>
              <td class="py-3 px-4 text-gray-600">{{ patient.iin }}</td>
              <td class="py-3 px-4 font-semibold" :class="patient.gender === 'M' ? 'text-blue-600' : 'text-red-600'">
                {{ patient.gender === 'M' ? $t('common.male') : $t('common.female') }}
              </td>
              <td v-if="userType === 'nurse'" class="py-3 px-4" @click.stop>
                <button 
                  @click="createVideoCall(patient)"
                  class="text-green-600 hover:text-green-800 text-sm font-medium transition-colors"
                >
                  {{ $t('nurse.callPatient') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div v-if="filteredClinicPatients.length > 5" class="mt-4 text-center">
          <p class="text-sm text-gray-600">
            {{ $t('common.showing') }} 5 {{ $t('common.of') }} {{ filteredClinicPatients.length }} {{ $t('navigation.patients').toLowerCase() }}
          </p>
        </div>
      </div>
      
      <div v-else class="text-center p-8 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
                  <p>{{ patientsSearchQuery ? $t('messages.noPatientsFound') : $t('messages.noClinicPatients') }}</p>
      </div>
    </div>
  </div>
  
  <!-- Cancellation Confirmation Dialog -->
  <div v-if="showCancellationDialog" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="closeCancellationDialog"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('appointment.cancelAppointment') }}</h2>
        </div>
        
        <div class="p-6">
          <p class="mb-4 text-gray-700">
            {{ $t('messages.cancellationConfirm') }}
          </p>
          
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('messages.cancellationReason') }}</label>
            <textarea 
              v-model="cancellationReason"
              class="form-textarea w-full rounded-md border-gray-300 shadow-sm"
              rows="3"
              :placeholder="$t('messages.cancellationReason')"
            ></textarea>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
          <button 
            @click="closeCancellationDialog"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {{ $t('common.cancel') }}
          </button>
          
          <button 
            @click="cancelAppointment"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            :disabled="cancellationLoading"
            :class="{'opacity-50 cursor-not-allowed': cancellationLoading}"
          >
            {{ cancellationLoading ? $t('common.loading') : $t('common.confirm') + ' ' + $t('appointment.cancelAppointment').toLowerCase() }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Consilium Invitation Dialog -->
  <div v-if="showConsiliumDialog" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="closeConsiliumDialog"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('consilium.inviteTitle') }}</h2>
        </div>
        
        <div class="p-6">
          <div v-if="consiliumDoctorsLoading" class="flex justify-center p-4">
            <HeartbeatLoader :text="$t('common.loading')" />
          </div>
          
          <div v-else-if="consiliumDoctorsError" class="p-4 bg-red-50 text-red-600 rounded-md">
            {{ consiliumDoctorsError }}
          </div>
          
          <div v-else>
            <p class="mb-4 text-gray-700">
              {{ $t('consilium.inviteDescription') }}
            </p>
            
            <div v-if="availableConsiliumDoctors.length === 0" class="p-4 bg-gray-50 text-gray-600 rounded-md">
              {{ $t('consilium.noDoctorsAvailable') }}
            </div>
            
            <div v-else class="space-y-2 max-h-60 overflow-y-auto">
              <div v-for="doctor in availableConsiliumDoctors" :key="doctor.id" 
                   class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-md">
                <input 
                  type="checkbox" 
                  :id="`doctor-${doctor.id}`"
                  v-model="selectedConsiliumDoctors"
                  :value="doctor.id"
                  class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                >
                <label :for="`doctor-${doctor.id}`" class="flex-1 cursor-pointer">
                  <div class="font-medium text-gray-900">{{ doctor.user.full_name }} - {{ formatDoctorSpecialization(doctor) || doctor.specialization }}</div>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
          <button 
            @click="closeConsiliumDialog"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {{ $t('common.cancel') }}
          </button>
          
          <button 
            @click="inviteToConsilium"
            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            :disabled="consiliumInviteLoading || selectedConsiliumDoctors.length === 0"
            :class="{'opacity-50 cursor-not-allowed': consiliumInviteLoading || selectedConsiliumDoctors.length === 0}"
          >
            {{ consiliumInviteLoading ? $t('common.loading') : $t('consilium.inviteButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Appointment Conclusion Modal -->
  <div v-if="showConclusionModal" 
       class="fixed inset-0 overflow-y-auto pointer-events-none"
       :class="dualModeActive ? 'z-[80]' : 'z-[80]'">
    <!-- Backdrop overlay - only show when not in dual mode -->
    <div v-if="!dualModeActive" class="fixed inset-0 bg-black bg-opacity-40 pointer-events-auto" @click="closeConclusionModal"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-start justify-center min-h-screen pt-8 px-4 transition-all duration-300 pointer-events-none"
         :class="{'!justify-end !p-4 !pt-4 h-full': dualModeActive}">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-auto z-10 max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 pointer-events-auto"
           :class="{'!w-[40%] !max-w-none !mx-0 !h-full !max-h-full': dualModeActive}">
        <div class="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-2xl font-bold text-gray-900">
                {{ $t('medical.conclusion.appointmentConclusion') }}
              </h2>
            </div>
            <div class="flex items-center space-x-2">
              <!-- Video warning indicator -->
              <div v-if="isVideoActive" class="flex items-center space-x-2 px-3 py-1 bg-yellow-100 rounded-full mr-2">
                <svg class="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-yellow-800 text-xs font-medium">Видео активно</span>
              </div>
              
              <button 
                @click="closeConclusionModal"
                class="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <div class="flex-1 overflow-y-auto">
          <div class="p-8 space-y-8">
            <!-- Информация о враче и пациенте -->
            <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
              <div class="grid grid-cols-2 gap-6">
                <!-- Информация о враче -->
                <div>
                  <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.doctorInfo') }}</h4>
                  <p class="text-lg font-medium text-gray-900">{{ formatDoctorName() }}</p>
                  <p class="text-sm text-gray-600">{{ formatDoctorSpecialization(doctorInfo) }}</p>
                </div>
                <!-- Информация о пациенте -->
                <div>
                  <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.patientInfo') }}</h4>
                  <p class="text-lg font-medium text-gray-900">{{ selectedAppointmentForConclusion ? getPatientName(selectedAppointmentForConclusion) : '' }}</p>
                  <p class="text-sm text-gray-600">
                    <span v-if="getPatientDetails(selectedAppointmentForConclusion)">
                      {{ getPatientDetails(selectedAppointmentForConclusion).birth_date ? formatDate(getPatientDetails(selectedAppointmentForConclusion).birth_date) : '' }}
                      <span v-if="getPatientIin(selectedAppointmentForConclusion)"> | ИИН: {{ getPatientIin(selectedAppointmentForConclusion) }}</span>
                    </span>
                  </p>
                  <p class="text-sm text-gray-500" v-if="getPatientFullAddress(selectedAppointmentForConclusion)">
                    {{ getPatientFullAddress(selectedAppointmentForConclusion) }}
                  </p>
                </div>
              </div>
              <!-- Дата приема -->
              <div class="mt-4 pt-4 border-t border-blue-100">
                <span class="text-sm font-medium text-gray-500">{{ $t('medical.conclusion.appointmentDateLabel') }}:</span>
                <span class="text-sm font-semibold text-gray-800 ml-2">
                  {{ selectedAppointmentForConclusion && selectedAppointmentForConclusion.slot ? formatDate(selectedAppointmentForConclusion.slot.date) : '' }}
                  {{ selectedAppointmentForConclusion && selectedAppointmentForConclusion.slot ? selectedAppointmentForConclusion.slot.start_time : '' }}
                </span>
              </div>
            </div>

            <!-- Заключение из МИС (свернутый блок) -->
            <div v-if="conclusionForm.conclusion_raw" class="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
              <h3 class="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <svg class="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Заключение из МИС Nadezhda
              </h3>
              <div class="bg-white rounded-xl border border-indigo-200 p-4 text-sm text-gray-800 font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                {{ conclusionForm.conclusion_raw }}
              </div>
            </div>

            <!-- Основная информация -->
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">Жалобы</label>
                <textarea 
                  v-model="conclusionForm.complaints"
                  class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterComplaints')"
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">Анамнез заболевания</label>
                <textarea 
                  v-model="conclusionForm.disease_history"
                  class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterDiseaseHistory')"
                ></textarea>
              </div>
            </div>

            <!-- Новые поля анамнезов -->
            <div class="bg-yellow-50 rounded-2xl p-6">
              <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('medical.conclusion.anamnesisSection') }}</h3>
              <div class="grid grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.allergyAnamnesis') }}</label>
                  <textarea 
                    v-model="conclusionForm.allergy_anamnesis"
                    class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                    :placeholder="$t('medical.conclusion.enterAllergyAnamnesis')"
                  ></textarea>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.epidemiologicalAnamnesis') }}</label>
                  <textarea 
                    v-model="conclusionForm.epidemiological_anamnesis"
                    class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                    :placeholder="$t('medical.conclusion.enterEpidemiologicalAnamnesis')"
                  ></textarea>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.transfusionAnamnesis') }}</label>
                  <textarea 
                    v-model="conclusionForm.transfusion_anamnesis"
                    class="w-full h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                    :placeholder="$t('medical.conclusion.enterTransfusionAnamnesis')"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Анамнез жизни (старые поля) -->
            <div class="bg-gray-50 rounded-2xl p-6">
              <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('medical.conclusion.lifeHistory') }}</h3>
              
              <!-- Анамнез жизни -->
              <div class="mb-6">
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.lifeAnamnesis') }}</label>
                <textarea 
                  v-model="conclusionForm.life_anamnesis"
                  class="w-full h-20 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none bg-white"
                  :placeholder="$t('medical.conclusion.enterLifeAnamnesis')"
                ></textarea>
              </div>
              
              <div class="space-y-4">
                <!-- Состоит на "Д" учете -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.dRegistration') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.d_registration === 'true'"
                        @change="conclusionForm.d_registration = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.d_registration === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.d_registration_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- TBS -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.tbs') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.tbs === 'true'"
                        @change="conclusionForm.tbs = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.tbs === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.tbs_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- Аллергоанамнез -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.allergies') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.allergies === 'true'"
                        @change="conclusionForm.allergies = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.allergies === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.allergies_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>

                <!-- Вирусные гепатиты -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.viralHepatitis') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.viral_hepatitis === 'true'"
                        @change="conclusionForm.viral_hepatitis = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.viral_hepatitis === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.viral_hepatitis_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Физикальные данные -->
            <div class="bg-blue-50 rounded-2xl p-6">
              <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('medical.conclusion.physicalDataSection') }}</h3>
              
              <!-- Общее состояние пациента -->
              <div class="mb-6">
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.generalCondition') }}</label>
                <textarea 
                  v-model="conclusionForm.general_condition"
                  class="w-full h-20 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none bg-white"
                  :placeholder="$t('medical.conclusion.enterGeneralCondition')"
                ></textarea>
              </div>
              
              <div class="grid grid-cols-4 gap-4 mb-6">
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.temperatureLabel') }}</label>
                  <input 
                    v-model="conclusionForm.temperature"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="36.6°C"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.bloodPressureLabel') }}</label>
                  <input 
                    v-model="conclusionForm.blood_pressure"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="120/80"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.pulseLabel') }}</label>
                  <input 
                    v-model="conclusionForm.pulse"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="70 уд/мин"
                  >
                </div>
                <div class="bg-white rounded-lg p-4">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('medical.conclusion.respiratoryRateLabel') }}</label>
                  <input 
                    v-model="conclusionForm.respiratory_rate"
                    type="text" 
                    class="w-full text-lg font-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
                    placeholder="16/мин"
                  >
                </div>
              </div>

              <div class="space-y-4">
                <!-- Отеки -->
                <div class="flex items-center space-x-4">
                  <div class="w-40 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.edema') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.edema === 'true'"
                        @change="conclusionForm.edema = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.edema === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.edema_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalDataPlaceholder')"
                    >
                  </div>
                </div>

                <!-- Кожный покров -->
                <div class="flex items-center space-x-4">
                  <div class="w-40 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.skinCondition') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.skin_condition === 'true'"
                        @change="conclusionForm.skin_condition = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.skin_condition === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.skin_condition_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalDataPlaceholder')"
                    >
                  </div>
                </div>

                <!-- Мочеиспускание и Стул -->
                <div class="grid grid-cols-2 gap-6">
                  <div class="bg-white rounded-lg p-4">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.urination') }}</label>
                    <select 
                      v-model="conclusionForm.urination"
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    >
                      <option value="">{{ $t('medical.conclusion.choose') }}</option>
                      <option value="норма">{{ $t('medical.conclusion.normal') }}</option>
                      <option value="затруднено">{{ $t('medical.conclusion.difficult') }}</option>
                      <option value="болезненно">{{ $t('medical.conclusion.painful') }}</option>
                      <option value="учащено">{{ $t('medical.conclusion.frequent') }}</option>
                    </select>
                  </div>
                  <div class="bg-white rounded-lg p-4">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{{ $t('medical.conclusion.stool') }}</label>
                    <select 
                      v-model="conclusionForm.stool"
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    >
                      <option value="">{{ $t('medical.conclusion.choose') }}</option>
                      <option value="норма">{{ $t('medical.conclusion.normal') }}</option>
                      <option value="запор">{{ $t('medical.conclusion.constipation') }}</option>
                      <option value="понос">{{ $t('medical.conclusion.diarrhea') }}</option>
                      <option value="неустойчивый">{{ $t('medical.conclusion.unstable') }}</option>
                    </select>
                  </div>
                </div>

                <!-- Выделение из половых органов -->
                <div class="flex items-center space-x-4">
                  <div class="w-44 flex-shrink-0">
                    <label class="text-sm font-medium text-gray-700">{{ $t('medical.conclusion.genitalDischarge') }}</label>
                  </div>
                  <div class="flex items-center space-x-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="conclusionForm.genital_discharge === 'true'"
                        @change="conclusionForm.genital_discharge = $event.target.checked ? 'true' : 'false'"
                        class="sr-only peer"
                      >
                      <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                    <span class="text-sm font-medium text-gray-600">{{ conclusionForm.genital_discharge === 'true' ? $t('medical.conclusion.yes') : $t('medical.conclusion.no') }}</span>
                  </div>
                  <div class="flex-1">
                    <input 
                      v-model="conclusionForm.genital_discharge_details"
                      type="text" 
                      class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      :placeholder="$t('medical.conclusion.additionalData')"
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Назначения и рекомендации -->
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.examinations') }}</label>
                <textarea 
                  v-model="conclusionForm.examinations"
                  class="w-full h-32 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                                      :placeholder="$t('medical.conclusion.enterExaminations')"
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-800 mb-3">{{ $t('medical.conclusion.recommendations') }}</label>
                <textarea 
                  v-model="conclusionForm.recommendations"
                  class="w-full h-32 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  :placeholder="$t('medical.conclusion.enterRecommendations')"
                ></textarea>
              </div>
            </div>

            <!-- Финальный протокол -->
            <div class="bg-green-50 rounded-2xl p-6">
              <div class="flex items-center justify-between mb-4">
                <label class="block text-lg font-bold text-gray-900">{{ $t('medical.conclusion.finalProtocol') }}</label>
                <button 
                  @click="updateFinalProtocol"
                  class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  {{ $t('medical.conclusion.updateProtocol') }}
                </button>
              </div>
              <textarea 
                v-model="conclusionForm.final_protocol"
                class="w-full h-40 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all resize-none bg-white"
                placeholder="Протокол генерируется автоматически на основе заполненных полей..."
                readonly
              ></textarea>
              <p class="text-xs text-gray-500 mt-2">
                <svg class="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                {{ $t('medical.conclusion.protocolAutoGenerated') }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="px-8 py-6 border-t border-gray-100 bg-white flex justify-between items-center rounded-b-2xl">
          <button 
            @click="copyProtocol"
            class="flex items-center space-x-2 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            <span class="font-medium">{{ $t('medical.conclusion.copyConclusion') }}</span>
          </button>
          
          <div class="flex space-x-4">
            <button 
              @click="closeConclusionModal"
              class="px-6 py-3 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
            >
              {{ $t('medical.conclusion.cancelConclusion') }}
            </button>
            <div class="relative">
              <button 
                @click="handleSaveAppointmentConclusion"
                :disabled="conclusionLoading"
                class="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                :class="{'opacity-50 cursor-not-allowed transform-none': conclusionLoading, 'cursor-pointer': isVideoActive}"
              >
                <svg v-if="conclusionLoading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <span class="font-medium">{{ conclusionLoading ? 'Сохранение...' : getConclusionButtonText() }}</span>
              </button>
              
              <!-- Tooltip warning when trying to save with active video -->
              <div v-if="showVideoWarningTooltip" 
                   class="absolute bottom-full right-0 mb-2 w-80 bg-yellow-50 border-2 border-yellow-400 rounded-lg shadow-xl p-4 z-[100] animate-fade-in-up">
                <div class="flex items-start space-x-3">
                  <svg class="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                  </svg>
                  <div class="flex-1">
                    <p class="text-sm font-semibold text-yellow-900 mb-1">Видеозвонок активен</p>
                    <p class="text-sm text-yellow-800">
                      Для завершения приема сначала выйдите из видеозвонка, нажав кнопку "Покинуть" в левом окне
                    </p>
                  </div>
                  <button @click="showVideoWarningTooltip = false" class="text-yellow-600 hover:text-yellow-800">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
                <!-- Arrow pointing to button -->
                <div class="absolute -bottom-2 right-8 w-4 h-4 bg-yellow-50 border-r-2 border-b-2 border-yellow-400 transform rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Room Records Dialog -->
  <div v-if="showRoomRecordsDialog" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-50" @click="closeRoomRecordsDialog"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('patientDetails.records.title') }}</h2>
          <p v-if="selectedAppointmentForRecords" class="text-sm text-gray-600 mt-1">
            {{ $t('patientDetails.records.roomName') }}: {{ selectedAppointmentForRecords.video_room_name || 'Скрыто до разрешения пациента' }}
          </p>
          <div class="flex space-x-3 mt-3">
            <button 
              @click="showChatForRoom"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
              :class="{
                'bg-blue-700': showChatPanel,
                'bg-gray-400 hover:bg-gray-400 cursor-not-allowed': mediaAccessNotice.visible
              }"
              :disabled="mediaAccessNotice.visible"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
              {{ showChatPanel ? $t('patientDetails.records.hideChat') : $t('patientDetails.records.showChat') }}
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <!-- Chat Panel Section -->
          <div v-if="showChatPanel" class="mb-6">
            <div class="bg-gray-50 rounded-lg border">
              <!-- Chat Header -->
              <div class="px-4 py-3 border-b bg-blue-50 rounded-t-lg">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg class="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    </svg>
                    <h4 class="font-medium text-gray-900">{{ $t('patientDetails.chat.meetingChatTitle') }}</h4>
                    
                  </div>
                  <button 
                    @click="closeChatPanel"
                    class="text-gray-500 hover:text-gray-700"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Chat Content -->
              <div class="p-4">
                <div v-if="roomChatLoading" class="flex justify-center p-4">
                  <HeartbeatLoader />
                  <span class="ml-2 text-sm text-gray-600">{{ $t('patientDetails.chat.loading') }}</span>
                </div>

                <div v-else-if="roomChatError" class="text-center p-4">
                  <div class="text-gray-500 text-sm">{{ roomChatError }}</div>
                </div>

                <div v-else-if="roomChatMessages.length > 0" class="space-y-3 max-h-64 overflow-y-auto">
                  <div 
                    v-for="message in roomChatMessages" 
                    :key="message.id"
                    class="flex flex-col"
                  >
                    <div class="flex items-start space-x-3">
                      <!-- Avatar -->
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                             :class="message.sender_name === 'Врач' || message.sender_name === 'Медсестра' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'">
                          {{ message.sender_name.charAt(0).toUpperCase() }}
                        </div>
                      </div>
                      
                      <!-- Message Content -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center space-x-2">
                          <span class="font-medium text-sm text-gray-900">{{ message.sender_name }}</span>
                          <span class="text-xs text-gray-500">{{ formatChatTimestamp(message.created_at) }}</span>
                        </div>
                        
                        <!-- Parsed Message Content -->
                        <div class="mt-1 p-3 rounded-lg max-w-md"
                             :class="message.sender_name === 'Врач' || message.sender_name === 'Медсестра' ? 'bg-blue-50 text-gray-800' : 'bg-gray-100 text-gray-800'">
                          
                          <!-- Text Message -->
                          <div v-if="getMessageText(message)" class="mb-2">
                            <p class="text-sm">{{ getMessageText(message) }}</p>
                          </div>
                          
                          <!-- File Attachments -->
                          <div v-if="getMessageFiles(message).length > 0" class="space-y-2">
                            <div 
                              v-for="file in getMessageFiles(message)" 
                              :key="file.id"
                              class="bg-white rounded-lg p-2 border"
                            >
                              <!-- Image Files -->
                              <div v-if="file.file_type && file.file_type.startsWith('image/')" class="space-y-2">
                                <img 
                                  :src="file.public_url" 
                                  :alt="file.original_name"
                                  class="max-w-full h-auto rounded max-h-40 cursor-pointer"
                                  @click="openImageModal(file.public_url, file.original_name)"
                                />
                                <div class="text-xs text-gray-500">
                                  📷 {{ file.original_name }} ({{ formatFileSize(file.file_size) }})
                                </div>
                              </div>
                              
                              <!-- Other Files -->
                              <div v-else class="flex items-center space-x-2">
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <div class="flex-1 min-w-0">
                                  <a 
                                    :href="file.public_url" 
                                    target="_blank"
                                    class="text-blue-600 hover:text-blue-800 text-sm font-medium truncate block"
                                  >
                                    {{ file.original_name }}
                                  </a>
                                  <div class="text-xs text-gray-500">{{ formatFileSize(file.file_size) }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Empty message indicator -->
                          <div v-else class="text-sm text-gray-500 italic">
                            Пустое сообщение
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center p-6">
                  <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    </svg>
                  </div>
                  <h4 class="text-sm font-medium text-gray-900 mb-1">Нет сообщений</h4>
                  <p class="text-sm text-gray-600">В этой встрече нет обмена сообщениями</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Video Player Section -->
          <div v-if="selectedVideoUrl && showVideoPlayer" class="mb-6">
            <div class="bg-black rounded-lg overflow-hidden">
              <video 
                :src="selectedVideoUrl" 
                controls 
                class="w-full h-64 md:h-96"
                controlsList="nodownload"
                @loadedmetadata="onVideoLoaded"
              >
                Ваш браузер не поддерживает воспроизведение видео.
              </video>
            </div>
            <div class="flex justify-between items-center mt-3">
              <div class="text-sm text-gray-600">
                <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                </svg>
                Воспроизведение записи встречи
              </div>
              <button 
                @click="closeVideoPlayer"
                class="text-gray-500 hover:text-gray-700 text-sm"
              >
                Закрыть плеер
              </button>
            </div>
          </div>
          
          <div v-if="roomRecordsLoading" class="flex justify-center p-6">
            <HeartbeatLoader />
            <span class="ml-2">Загружаем записи...</span>
          </div>

          <div v-else-if="mediaAccessNotice.visible" class="text-center p-8">
            <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ mediaAccessNotice.title }}</h3>
            <p class="text-gray-600 mb-5">{{ mediaAccessNotice.description }}</p>
            <div class="flex justify-center gap-3">
              <button
                v-if="mediaAccessNotice.canRequest"
                @click="requestMediaAccessForSelectedAppointment"
                :disabled="mediaAccessNotice.requestSending"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {{ mediaAccessNotice.requestSending ? 'Отправляем запрос...' : 'Запросить доступ к видео и чату' }}
              </button>
              <button
                @click="showRoomRecords(selectedAppointmentForRecords)"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Проверить статус
              </button>
            </div>
          </div>
          
          <div v-else-if="roomRecordsError" class="text-center p-8">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('patientDetails.records.error') }}</h3>
            <p class="text-red-600 mb-4">{{ roomRecordsError }}</p>
            <button 
              @click="showRoomRecords(selectedAppointmentForRecords)"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {{ $t('common.retry') }}
            </button>
          </div>
          
          <div v-else-if="roomRecords.length > 0">
            <div class="flex items-center justify-between mb-4">
              <p class="text-gray-700">
                {{ $t('patientDetails.records.foundRecords') }}: {{ roomRecords.length }}
              </p>
              <div class="text-sm text-gray-500">
                <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ $t('patientDetails.records.clickToPlay') }}
              </div>
            </div>
            
            <div class="grid grid-cols-1 gap-3">
              <div 
                v-for="(record, index) in roomRecords" 
                :key="`record-${index}`" 
                class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div class="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <svg class="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">Запись встречи {{ index + 1 }}</h4>
                      <p class="text-sm text-gray-500">Видеофайл в формате MP4</p>
                      <div class="flex items-center mt-1 text-xs text-gray-400">
                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                        {{ $t('patientDetails.records.readyToView') }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex space-x-2">
                    <button 
                      @click="playVideo(record)"
                      class="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                      {{ $t('common.view') }}
                    </button>
                    <a 
                      :href="record"
                      target="_blank"
                      class="px-4 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                      {{ $t('common.open') }}
                    </a>
                    <a 
                      :href="record"
                      :download="`meeting-record-${index + 1}.mp4`"
                      class="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      {{ $t('common.download') }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center p-8">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('patientDetails.records.notFound') }}</h3>
                          <p class="text-gray-600">{{ $t('patientDetails.records.noRecordsAvailable') }}</p>
          </div>
        </div>
        
        <div class="px-6 py-3 border-t bg-gray-50 flex justify-end rounded-b-lg">
          <button 
            @click="closeRoomRecordsDialog"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast Notifications -->
  <div v-if="toasts.length > 0" class="fixed top-4 right-4 z-50 space-y-2">
    <div 
      v-for="toast in toasts" 
      :key="toast.id"
      class="max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 ease-in-out"
      :class="{
        'translate-x-0 opacity-100': toast.show,
        'translate-x-full opacity-0': !toast.show
      }"
    >
      <div class="flex items-center p-4">
        <div class="flex-shrink-0">
          <div v-if="toast.type === 'success'" class="w-6 h-6 text-green-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else-if="toast.type === 'error'" class="w-6 h-6 text-red-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div v-else class="w-6 h-6 text-blue-600">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium text-gray-900">{{ toast.title }}</p>
          <p v-if="toast.message" class="text-sm text-gray-600 mt-1">{{ toast.message }}</p>
        </div>
        <div class="ml-4 flex-shrink-0">
          <button 
            @click="removeToast(toast.id)"
            class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Image Modal -->
  <div v-if="showImageModal" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black opacity-75" @click="closeImageModal"></div>
    
    <!-- Modal dialog -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-4xl max-h-full mx-auto z-10 relative">
        <div class="p-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium text-gray-900">{{ modalImageAlt || $t('common.image') }}</h3>
            <button 
              @click="closeImageModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="max-h-96 overflow-auto">
            <img 
              :src="modalImageUrl" 
              :alt="modalImageAlt"
              class="max-w-full h-auto rounded"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Модальное окно видеокомнаты -->
  <VideoRoomModal
    :show="showVideoRoomModal"
    :roomUrl="currentVideoRoomUrl"
    :appointment="currentAppointment"
    :dualMode="dualModeActive"
    @close="closeVideoRoomModal"
    @open-conclusion="openConclusionFromVideo"
    @open-consilium="openConsiliumDialog"
    @video-active-changed="handleVideoActiveChanged"
  />
</template>

<script>
import api from '../services/api'
import staffApi from '../services/staffApi'
import HeartbeatLoader from '../components/common/HeartbeatLoader.vue'
import VideoRoomModal from '../components/common/VideoRoomModal.vue'
import { formatDoctorSpecialization as formatDoctorSpecializationText } from '../utils/doctorDisplay'

export default {
  name: 'HomePage',
  components: {
    HeartbeatLoader,
    VideoRoomModal
  },
  data() {
    return {
      doctorInfo: null,
      userType: null,
      clinicPatients: [],
      patientsLoading: false,
      patientsError: null,
      activeAppointmentsTab: 'upcoming',
      appointments: [],
      appointmentsLoading: false,
      appointmentsError: null,
      // Данные для статистики за все время
      allAppointments: [],
      allSlotsData: {},
      // Cancellation dialog
      showCancellationDialog: false,
      appointmentToCancel: null,
      cancellationReason: '',
      cancellationLoading: false,
      slotsData: {},
      patientsData: {},
      doctorsData: {},
      nursesData: {},
      visitReasonsData: {},
      // Room records dialog
      showRoomRecordsDialog: false,
      roomRecordsLoading: false,
      roomRecords: [],
      selectedAppointmentForRecords: null,
      roomRecordsError: null,
      mediaAccessNotice: {
        visible: false,
        status: null,
        title: '',
        description: '',
        canRequest: false,
        requestSending: false
      },
      // Video player state
      selectedVideoUrl: null,
      showVideoPlayer: false,
      // Chat state
      roomChatMessages: [],
      roomChatLoading: false,
      roomChatError: null,
      showChatPanel: false,
      // Image modal state
      showImageModal: false,
      modalImageUrl: null,
      modalImageAlt: null,
      // Conclusion modal state
      conclusionLoading: false,
      patientsSearchQuery: '',
      // Patient modal
      showPatientModal: false,
      selectedPatient: null,
      selectedPatientDetails: null,
      healthTrackerEntries: [],
      healthTrackerLoading: false,
      healthTrackerError: null,
      showAllTrackerEntries: false,
      activePatientTab: 'info',
      patientAppointments: [],
      patientAppointmentsLoading: false,
      patientAppointmentsError: null,
      // Auto-refresh
      autoRefreshInterval: null,
      toasts: [],
      // Consilium dialog
      showConsiliumDialog: false,
      selectedAppointmentForConsilium: null,
      availableConsiliumDoctors: [],
      selectedConsiliumDoctors: [],
      consiliumDoctorsLoading: false,
      consiliumDoctorsError: null,
      consiliumInviteLoading: false,
      // Video checking for no-show appointments
      checkingVideoStatus: {},
      // Appointment conclusion modal
      showConclusionModal: false,
      selectedAppointmentForConclusion: null,
      conclusionForm: {
        // Basic information
        complaints: '',
        disease_history: '',
        
        // New anamnesis fields
        life_anamnesis: '',
        allergy_anamnesis: '',
        epidemiological_anamnesis: '',
        transfusion_anamnesis: '',
        general_condition: '',
        
        // Life history (old fields)
        d_registration: '',
        d_registration_details: '',
        tbs: '',
        tbs_details: '',
        allergies: '',
        allergies_details: '',
        viral_hepatitis: '',
        viral_hepatitis_details: '',
        
        // Physical data
        temperature: '',
        blood_pressure: '',
        pulse: '',
        respiratory_rate: '',
        edema: '',
        edema_details: '',
        skin_condition: '',
        skin_condition_details: '',
        urination: '',
        stool: '',
        genital_discharge: '',
        genital_discharge_details: '',
        
        // Conclusions
        examinations: '',
        recommendations: '',
        final_protocol: ''
      },
      // Timeout для debounced обновления статистик
      statsUpdateTimeout: null,
      // Video room modal
      showVideoRoomModal: false,
      currentVideoRoomUrl: '',
      currentAppointment: null,
      isVideoActive: false,
      dualModeActive: false,
      // Video warning tooltip
      showVideoWarningTooltip: false
    }
  },
  computed: {
    showClinicPatientsSection() {
      if (this.userType === 'nurse') return true
      if (this.userType === 'doctor') return !!(this.doctorInfo && this.doctorInfo.clinic)
      return false
    },
    filteredClinicPatients() {
      if (!this.patientsSearchQuery) return this.clinicPatients
      
      const query = this.patientsSearchQuery.toLowerCase()
      return this.clinicPatients.filter(patient => {
        const name = this.getPatientFullName(patient).toLowerCase()
        const iin = patient.iin?.toLowerCase() || ''
        
        return name.includes(query) || iin.includes(query)
      })
    }
  },
  async created() {
    await this.fetchDoctorInfo();
    if (this.showClinicPatientsSection) {
      this.fetchClinicPatients();
    }
    this.fetchAppointments();
    this.fetchAllAppointmentsForStats(); // Загружаем все данные для статистики
    this.fetchVisitReasons(); // Загружаем причины визитов
    
    // Запускаем автоматическое обновление приемов
    this.startAutoRefresh();
  },
  beforeUnmount() {
    // Останавливаем автоматическое обновление при уходе со страницы
    this.stopAutoRefresh();
    // Очищаем timeout для обновления статистик
    if (this.statsUpdateTimeout) {
      clearTimeout(this.statsUpdateTimeout);
    }
  },
  watch: {
    activeAppointmentsTab() {
      this.fetchAppointments();
    },
    // Автоматическое обновление финального протокола при изменении полей
    'conclusionForm.complaints'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.disease_history'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.d_registration'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.d_registration_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.tbs'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.tbs_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.allergies'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.allergies_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.viral_hepatitis'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.viral_hepatitis_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.temperature'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.blood_pressure'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.pulse'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.respiratory_rate'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.edema'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.edema_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.skin_condition'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.skin_condition_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.urination'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.stool'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.genital_discharge'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.genital_discharge_details'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.examinations'() {
      this.updateFinalProtocol();
    },
    'conclusionForm.recommendations'() {
      this.updateFinalProtocol();
    }
  },
  methods: {
    async fetchDoctorInfo() {
      try {
        // Сначала проверяем тип пользователя
        const authResponse = await api.checkAuth()
        const userType = authResponse.data.user?.type
        
        // Загружаем профиль в зависимости от типа пользователя
        let response
        if (userType === 'doctor') {
          response = await api.getDoctorProfile()
        } else if (userType === 'nurse') {
          response = await api.getNurseProfile()
        }
        
        this.doctorInfo = response.data
        this.userType = userType
      } catch (error) {
      // Error handled silently
      }
    },
    formatDoctorName() {
      if (!this.doctorInfo || !this.doctorInfo.user) return ''
      
      const user = this.doctorInfo.user
      const parts = []
      
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)
      
      return parts.join(' ') || 'Врач'
    },
    formatDoctorSpecialization(doctor) {
      return formatDoctorSpecializationText(doctor)
    },
    formatDoctorLocation() {
      if (!this.doctorInfo) return ''
      
      const parts = []
      if (this.doctorInfo.city?.name) parts.push(this.doctorInfo.city.name)
      if (this.doctorInfo.clinic) parts.push(this.getLocalizedClinicName(this.doctorInfo.clinic))
      
      return parts.join(', ')
    },
    getLocalizedClinicName(clinic) {
      if (this.$i18n.locale === 'kk' && clinic.name_kz) {
        return clinic.name_kz;
      }
      // Возвращаем русское название или обычное name как fallback
      return clinic.name || '';
    },
    getTodayAppointmentsCount() {
      if (!Array.isArray(this.allAppointments)) return 0
      
      const today = new Date().toISOString().split('T')[0]
      return this.allAppointments.filter(appointment => {
        const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.allSlotsData[appointment.slot]
        if (!slot) return false
        
        return slot.date === today
      }).length
    },
    getUpcomingAppointmentsCount() {
      if (!Array.isArray(this.allAppointments)) return 0
      
      const today = new Date().toISOString().split('T')[0]
      return this.allAppointments.filter(appointment => {
        const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.allSlotsData[appointment.slot]
        if (!slot) return false
        
        return slot.date > today && appointment.status === 'scheduled'
      }).length
    },
    getCompletedAppointmentsCount() {
      if (!Array.isArray(this.allAppointments)) return 0
      
      return this.allAppointments.filter(appointment => {
        // Считаем завершенными приемы со статусами: completed, patient_no_show, no_show
        const completedStatuses = ['completed', 'patient_no_show', 'no_show']
        return completedStatuses.includes(appointment.status)
      }).length
    },
    async fetchClinicPatients() {
      try {
        this.patientsLoading = true;
        // Используем соответствующий API в зависимости от типа пользователя
        // Загружаем все записи для главной страницы (без пагинации)
        let response
        if (this.userType === 'doctor') {
          response = await api.getDoctorClinicPatients({ page_size: 1000 });
        } else if (this.userType === 'nurse') {
          response = await api.getNurseClinicPatients({ page_size: 1000 });
        }
        // Handle both new paginated format and old array format
        if (response.data && response.data.results) {
          this.clinicPatients = response.data.results;
        } else if (Array.isArray(response.data)) {
          this.clinicPatients = response.data;
        } else {
          this.clinicPatients = [];
        }
        this.patientsError = null;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          if (error.response.data && error.response.data.detail) {
            this.patientsError = error.response.data.detail;
          } else {
            this.patientsError = 'Не удалось найти пациентов для вашей клиники.';
          }
        } else {
          this.patientsError = 'Не удалось загрузить список пациентов. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.patientsLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '' // Проверка на Invalid Date
      return date.toLocaleDateString('ru-RU')
    },
    formatTime(timeString) {
      if (!timeString) return '';
      
      // If the string is already in format like "08:00:00"
      if (typeof timeString === 'string' && timeString.includes(':')) {
        return timeString.substring(0, 5); // Return first 5 characters (HH:MM)
      }
      
      // If it's a date object or date string
      try {
        const time = new Date(timeString);
        return time.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return '';
      }
    },
    getStatusClass(status) {
      switch (status) {
        case 'scheduled':
          return 'bg-blue-100 text-blue-700'
        case 'in_progress':
          return 'bg-green-100 text-green-700'
        case 'completed':
          return 'bg-gray-100 text-gray-700'
        case 'cancelled':
          return 'bg-red-100 text-red-700'
        case 'no_show':
          return 'bg-orange-100 text-orange-700'
        case 'patient_no_show':
          return 'bg-orange-100 text-orange-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },
    getStatusText(status) {
      switch (status) {
        case 'scheduled':
          return this.$t('appointment.scheduled')
        case 'in_progress':
          return this.$t('appointment.inProgress')
        case 'completed':
          return this.$t('appointment.completed')
        case 'cancelled':
          return this.$t('appointment.cancelled')
        case 'no_show':
          return this.$t('appointment.no_show')
        case 'patient_no_show':
          return this.$t('appointment.patient_no_show')
        default:
          return this.$t('appointment.unknownStatus')
      }
    },
    // Display status methods - temporarily show no_show statuses as in_progress
    getDisplayStatusClass(status) {
      // Temporarily show no_show and patient_no_show as in_progress
      if (status === 'no_show' || status === 'patient_no_show') {
        return 'bg-green-100 text-green-700'
      }
      return this.getStatusClass(status)
    },
    getDisplayStatusText(status) {
      // Temporarily show no_show and patient_no_show with custom text "В обработке"
      if (status === 'no_show' || status === 'patient_no_show') {
        return 'В обработке'  // Hardcoded to differentiate from actual "in_progress" status
      }
      return this.getStatusText(status)
    },
    async fetchAppointments() {
      try {
        this.appointmentsLoading = true;
        
        // Используем staff API для получения приемов врача (БЕЗ приемов где он пациент)
        // Используем специальные эндпоинты для upcoming/today/past вместо параметра status
        let response;
        if (this.activeAppointmentsTab === 'upcoming') {
          response = await staffApi.getStaffUpcomingAppointments();
        } else if (this.activeAppointmentsTab === 'today') {
          response = await staffApi.getStaffTodayAppointments();
        } else if (this.activeAppointmentsTab === 'past') {
          response = await staffApi.getStaffPastAppointments();
        } else {
          // Для других статусов (scheduled, completed, cancelled, etc.) используем params
          const params = { status: this.activeAppointmentsTab };
          response = await staffApi.getStaffAppointments(params);
        }
        
        // Handle both paginated and non-paginated responses
        let appointmentsData = [];
        if (response.data && Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          appointmentsData = response.data.results;
        } else {
      // No action needed
        }
        
        // Set the appointments directly
        this.appointments = appointmentsData;
        
        // After setting appointments, we need to fetch additional data
        await this.fetchAppointmentDetails();
        
        this.appointmentsError = null;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          if (error.response.data && error.response.data.detail) {
            this.appointmentsError = error.response.data.detail;
          } else {
            this.appointmentsError = 'Не удалось загрузить список приемов. Пожалуйста, попробуйте позже.';
          }
        } else {
          this.appointmentsError = 'Не удалось загрузить список приемов. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.appointmentsLoading = false;
      }
    },
    async fetchAppointmentDetails() {
      // Build slot data from appointments (slots are now included in appointment response)
      this.slotsData = {};
      this.patientsData = {};
      
      // Extract slot data from appointments
      this.appointments.forEach(appointment => {
        if (appointment.slot && typeof appointment.slot === 'object') {
          this.slotsData[appointment.slot.id] = appointment.slot;
        }
      });
      
      // Fetch patient data
      const patientIds = this.appointments.map(a => a.patient).filter(id => id);
      
      // Fetch patient details with full name information
      if (patientIds.length > 0) {
        try {
          for (const patientId of patientIds) {
            try {
              // Пробуем получить детальную информацию пациента
              const response = await api.getPatientById(patientId);
              if (response.data) {
                this.patientsData[patientId] = response.data;
              }
            } catch (patientError) {
              
              // Если не удалось получить детальную информацию, ищем в списке пациентов участка
              const clinicPatient = this.clinicPatients.find(p => p.id === patientId);
                              if (clinicPatient) {
                  this.patientsData[patientId] = clinicPatient;
              } else {
                // Create a placeholder with patient ID
                this.patientsData[patientId] = { 
                  id: patientId,
                  full_name: `Пациент #${patientId}`
                };
              }
            }
          }
        } catch (error) {
      // Error handled silently
        }
      }
    },
    getPatientName(appointment) {
      if (!appointment) return this.$t('appointment.unknownPatient');
      
      // 1) Если из API уже пришла строка с ФИО пациента, используем её
      if (appointment.patient_name) {
        return appointment.patient_name;
      }
      
      // 2) Если в объекте appointment вложен patient.user.full_name, возвращаем его
      if (appointment.patient && appointment.patient.user && appointment.patient.user.full_name) {
        return appointment.patient.user.full_name;
      }
      
      const patientId = appointment.patient;
      if (!patientId) return this.$t('appointment.unknownPatient');
      
      // 3) Проверяем, есть ли подробные данные пациента в локальном кэше
      const patientData = this.patientsData[patientId];
      if (patientData) {
        const nameParts = [];
        
        // Сначала пробуем из основных полей пациента
        if (patientData.last_name) nameParts.push(patientData.last_name);
        if (patientData.first_name) nameParts.push(patientData.first_name);
        if (patientData.middle_name) nameParts.push(patientData.middle_name);
        
        // Если основные поля пустые, пробуем из user объекта
        if (nameParts.length === 0 && patientData.user) {
          if (patientData.user.last_name) nameParts.push(patientData.user.last_name);
          if (patientData.user.first_name) nameParts.push(patientData.user.first_name);
          if (patientData.user.middle_name) nameParts.push(patientData.user.middle_name);
        }
        
        // Если есть готовое полное имя, используем его
        if (nameParts.length === 0) {
          if (patientData.full_name) return patientData.full_name;
          if (patientData.user && patientData.user.full_name) return patientData.user.full_name;
        }
        
        if (nameParts.length > 0) {
          return nameParts.join(' ');
        }
      }
      
      // 4) Финальный резерв – выводим «Пациент #ID»
      return `${this.$t('common.patient')} #${patientId}`;
    },
    getPatientAddress(patient) {
      if (!patient) return '';
      const parts = [];
      if (patient.address_district) parts.push(patient.address_district);
      if (patient.address_street) parts.push(`ул. ${patient.address_street}`);
      if (patient.address_house) parts.push(`д. ${patient.address_house}`);
      if (patient.address_apartment) parts.push(`кв. ${patient.address_apartment}`);
      return parts.join(', ');
    },
    getPatientDetails(appointment) {
      if (!appointment) return null;
      // Если patient - это объект с данными
      if (appointment.patient && typeof appointment.patient === 'object') {
        return appointment.patient;
      }
      // Если patient - это ID, ищем в кэше
      const patientId = appointment.patient;
      if (patientId && this.patientsData[patientId]) {
        return this.patientsData[patientId];
      }
      return null;
    },
    getPatientIin(appointment) {
      const patient = this.getPatientDetails(appointment);
      if (!patient) return null;
      // Проверяем разные пути к ИИН
      if (patient.user && patient.user.iin) return patient.user.iin;
      if (patient.iin) return patient.iin;
      return null;
    },
    getPatientFullAddress(appointment) {
      const patient = this.getPatientDetails(appointment);
      if (!patient) return '';
      return this.getPatientAddress(patient);
    },
    getSlotDate(appointment) {
      if (!appointment) return '';
      
      // Get slot data from appointment object or cache
      const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot];
      return slot ? slot.date : '';
    },
    getSlotStartTime(appointment) {
      if (!appointment) return '';
      
      // Get slot data from appointment object or cache
      const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot];
      return slot ? slot.start_time : '';
    },
    getSlotEndTime(appointment) {
      if (!appointment) return '';
      
      // Get slot data from appointment object or cache
      const slot = appointment.slot && typeof appointment.slot === 'object' ? appointment.slot : this.slotsData[appointment.slot];
      return slot ? slot.end_time : '';
    },
    canStartConsultation(appointment) {
      return appointment.status === 'scheduled'
    },
    canCancelAppointment(appointment) {
      // Only main doctor can cancel appointment, not consilium doctors
      if (!this.doctorInfo || !this.doctorInfo.user) return false;
      
      const currentUserId = this.doctorInfo.user.id;
      const isMainDoctor = appointment.doctor && appointment.doctor.user && 
                          appointment.doctor.user.id === currentUserId;
      
      return (appointment.status === 'scheduled' || appointment.status === 'in_progress') && isMainDoctor;
    },
    canCompleteAppointment(appointment) {
      // Only main doctor can complete appointment, not consilium doctors
      if (!this.doctorInfo || !this.doctorInfo.user) return false;
      
      const currentUserId = this.doctorInfo.user.id;
      
      // Check if current user is the main doctor
      // appointment.doctor может быть либо объектом, либо просто ID
      let isMainDoctor = false;
      
      if (appointment.doctor) {
        if (typeof appointment.doctor === 'object' && appointment.doctor.user) {
          // Если appointment.doctor - объект с полем user
          isMainDoctor = appointment.doctor.user.id === currentUserId;
        } else if (typeof appointment.doctor === 'number') {
          // Если appointment.doctor - просто ID врача, сравниваем с ID врача
          isMainDoctor = appointment.doctor === this.doctorInfo.id;
        }
      }
      
      // Также проверяем appointment.user как fallback
      if (!isMainDoctor && appointment.user) {
        isMainDoctor = appointment.user.id === currentUserId;
      }
      
      return appointment.status === 'in_progress' && isMainDoctor;
    },
    canLeaveConsilium(appointment) {
      // Only consilium doctors can leave consilium
      if (!this.doctorInfo || !this.doctorInfo.user) return false;
      
      const currentUserId = this.doctorInfo.user.id;
      
      if (!appointment.is_consilium || !appointment.consilium_doctors) {
        return false;
      }
      
      // Check if current user is a consilium doctor (not the main doctor)
      const isConsiliumDoctor = appointment.consilium_doctors.some(doctor => 
        doctor.user && doctor.user.id === currentUserId
      );
      const isMainDoctor = appointment.doctor && appointment.doctor.user && 
                          appointment.doctor.user.id === currentUserId;
      
      return isConsiliumDoctor && !isMainDoctor && 
             (appointment.status === 'scheduled' || appointment.status === 'in_progress');
    },
    confirmCancelAppointment(appointment) {
      this.appointmentToCancel = appointment;
      this.showCancellationDialog = true;
      this.cancellationReason = '';
    },
    async cancelAppointment() {
      if (!this.appointmentToCancel || this.cancellationLoading) return;
      
      this.cancellationLoading = true;
      
      try {
        // The API call to cancel the appointment
        await api.cancelAppointment(this.appointmentToCancel.id);
        
        // Update the status in the local list to avoid needing to refresh
        const index = this.appointments.findIndex(a => a.id === this.appointmentToCancel.id);
        if (index !== -1) {
          this.appointments[index].status = 'cancelled';
        }
        
        // Close the dialog
        this.closeCancellationDialog();
        
        // Optionally refresh the appointments list
        await this.fetchAppointments();
      } catch (error) {
        // Handle error (you might want to show an error message to the user)
      } finally {
        this.cancellationLoading = false;
      }
    },
    closeCancellationDialog() {
      this.showCancellationDialog = false;
    },
    // Consilium methods
    async openConsiliumDialog(appointment) {
      this.selectedAppointmentForConsilium = appointment;
      this.selectedConsiliumDoctors = [];
      this.showConsiliumDialog = true;
      
      // Load available doctors
      await this.loadConsiliumDoctors();
    },
    closeConsiliumDialog() {
      this.showConsiliumDialog = false;
      this.selectedAppointmentForConsilium = null;
      this.availableConsiliumDoctors = [];
      this.selectedConsiliumDoctors = [];
      this.consiliumDoctorsError = null;
    },
    async loadConsiliumDoctors() {
      this.consiliumDoctorsLoading = true;
      this.consiliumDoctorsError = null;
      
      try {
        const response = await api.getClinicDoctorsForConsilium();
        this.availableConsiliumDoctors = response.data || [];
      } catch (error) {
        this.consiliumDoctorsError = 'Не удалось загрузить список врачей клиники';
      } finally {
        this.consiliumDoctorsLoading = false;
      }
    },
    async inviteToConsilium() {
      if (!this.selectedAppointmentForConsilium || this.selectedConsiliumDoctors.length === 0) {
        return;
      }
      
      this.consiliumInviteLoading = true;
      
      try {
        await api.inviteToConsilium(
          this.selectedAppointmentForConsilium.id, 
          this.selectedConsiliumDoctors
        );
        
        // Show success notification
        this.showNotification(this.$t('consilium.inviteSuccess'), 'success');
        
        // Close dialog and refresh appointments
        this.closeConsiliumDialog();
        await this.fetchAppointments();
        
      } catch (error) {
        let errorMessage = this.$t('consilium.inviteError');
        
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.showNotification(errorMessage, 'error');
      } finally {
        this.consiliumInviteLoading = false;
      }
    },
    confirmLeaveConsilium(appointment) {
      if (confirm(this.$t('consilium.leaveConsiliumConfirm'))) {
        this.leaveConsilium(appointment);
      }
    },
    async leaveConsilium(appointment) {
      try {
        await api.leaveConsilium(appointment.id);
        
        // Show success notification
        this.showNotification(this.$t('consilium.leaveConsiliumSuccess'), 'success');
        
        // Refresh appointments to show updated data
        await this.fetchAppointments();
        
      } catch (error) {
        let errorMessage = this.$t('consilium.leaveConsiliumError');
        
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.showNotification(errorMessage, 'error');
      }
    },
    async startConsultation(appointment) {
      try {
        
        // Первый шаг: Создаем видеокомнату
        const videoRoomResponse = await api.createVideoRoom();
        
        if (!videoRoomResponse.data || videoRoomResponse.data.result !== 'success' || !videoRoomResponse.data.data) {
          throw new Error('Failed to create video room: Invalid API response');
        }
        
        const roomName = videoRoomResponse.data.data.room_name;
        const roomUrl = videoRoomResponse.data.data.url;
        
        
        // Второй шаг: Обновляем статус приема и сохраняем данные видеокомнаты
        const updateData = {
          status: 'in_progress',
          video_room_url: roomUrl,
          video_room_name: roomName
        };
        
        await staffApi.updateStaffAppointmentVideoRoom(appointment.id, updateData);
        
        // Синхронизируем URL видеокомнаты в МИС в фоне (не блокируем UI)
        staffApi.syncUrlToMIS(appointment.id).catch(misError => {
          console.warn('MIS URL sync failed:', misError);
        });
        
        // Обновляем локальные данные
        const index = this.appointments.findIndex(a => a.id === appointment.id);
        if (index !== -1) {
          this.appointments[index].status = 'in_progress';
          this.appointments[index].video_room_url = roomUrl;
          this.appointments[index].video_room_name = roomName;
        }
        
        // Добавляем параметры username и language к URL
        const doctorName = this.formatDoctorName();
        const url = new URL(roomUrl);
        url.searchParams.set('username', doctorName);
        
        // Add language parameter
        const currentLanguage = this.$i18n.locale;
        if (currentLanguage === 'kk') {
          url.searchParams.set('lang', 'kk');
        } else {
          url.searchParams.set('lang', 'ru');
        }
        
        // Открываем видеокомнату в модальном окне
        this.currentVideoRoomUrl = url.toString();
        this.currentAppointment = appointment;
        this.showVideoRoomModal = true;
        this.isVideoActive = true;
        
        // Обновляем список приемов
        await this.fetchAppointments();
        
      } catch (error) {
        // Показываем более детальное сообщение об ошибке
        if (error.message && error.message.includes('видеокомнату')) {
          alert(error.message);
        } else {
          alert('Произошла ошибка при начале приема. Пожалуйста, попробуйте позже.');
        }
      }
    },
    async joinVideoConsultation(videoRoomUrl, appointment) {
      try {
        // Add username query parameter with doctor's full name
        const doctorName = this.formatDoctorName();
        const url = new URL(videoRoomUrl);
        url.searchParams.set('username', doctorName);
        
        // Add language parameter
        const currentLanguage = this.$i18n.locale;
        if (currentLanguage === 'kk') {
          url.searchParams.set('lang', 'kk');
        } else {
          url.searchParams.set('lang', 'ru');
        }
        
        // Open in modal instead of new tab
        this.currentVideoRoomUrl = url.toString();
        this.currentAppointment = appointment;
        this.showVideoRoomModal = true;
        this.isVideoActive = true;
      } catch (error) {
        alert('Не удалось присоединиться к видеоприему. Пожалуйста, попробуйте позже.');
      }
    },
    closeVideoRoomModal() {
      this.showVideoRoomModal = false;
      this.currentVideoRoomUrl = '';
      this.currentAppointment = null;
      this.isVideoActive = false;
      this.dualModeActive = false;
    },
    openConclusionFromVideo() {
      if (this.currentAppointment) {
        this.dualModeActive = true;
        this.selectedAppointmentForConclusion = this.currentAppointment;
        this.resetConclusionForm();
        this.showConclusionModal = true;
      }
    },
    handleVideoActiveChanged(isActive) {
      this.isVideoActive = isActive;
    },
    closeConclusionWhileDual() {
      this.showConclusionModal = false;
      this.dualModeActive = false;
    },

    async shareRoomLink(appointment) {
      try {
        if (!appointment.video_room_url) {
          alert(this.$t('appointment.roomLinkUnavailable'));
          return;
        }
        
        // Добавляем языковой параметр и копируем ссылку в буфер обмена
        const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
        const urlWithLanguage = addLanguageToVideoRoomUrl(appointment.video_room_url, this.$i18n.locale);
        await navigator.clipboard.writeText(urlWithLanguage);
        
        // Показываем уведомление об успешном копировании
        this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        
      } catch (error) {
        
        // Fallback для браузеров без поддержки clipboard API
        try {
          const textArea = document.createElement('textarea');
          textArea.value = appointment.video_room_url;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        } catch (fallbackError) {
          alert('Не удалось скопировать ссылку. Попробуйте выделить и скопировать вручную: ' + appointment.video_room_url);
        }
      }
    },
    async completeConsultation(appointment) {
      try {
        
        // Open the conclusion modal instead of directly completing
        this.selectedAppointmentForConclusion = appointment;
        this.resetConclusionForm();
        this.showConclusionModal = true;
      } catch (error) {
        alert('Произошла ошибка при открытии формы заключения.');
      }
    },
    async showRoomRecords(appointment) {
      try {
        if (!appointment || !this.hasAppointmentMedia(appointment)) {
          this.roomRecordsError = 'Для этого приема нет доступных медиа-данных.';
          this.showRoomRecordsDialog = true;
          this.selectedAppointmentForRecords = appointment;
          this.resetMediaAccessNotice();
          return;
        }

        // Устанавливаем состояние диалога
        this.showRoomRecordsDialog = true;
        this.selectedAppointmentForRecords = appointment;
        this.roomRecordsLoading = true;
        this.roomRecords = [];
        this.roomRecordsError = null;
        this.resetMediaAccessNotice();

        const accessResponse = await staffApi.getAppointmentMediaAccess(appointment.id);
        const mediaAccess = accessResponse.data || {};

        if (!mediaAccess.can_view) {
          this.setMediaAccessNotice(mediaAccess);
          return;
        }

        const response = await staffApi.getAppointmentMediaRecords(appointment.id);
        
        if (response.data && response.data.result === 'success' && response.data.data) {
          this.roomRecords = response.data.data.record_urls || [];
        } else if (response.data && response.data.result === 'error') {
          this.roomRecordsError = response.data.error || 'Ошибка получения записей';
        } else {
          this.roomRecordsError = 'Неожиданный формат ответа сервера';
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.media_access) {
          this.setMediaAccessNotice(error.response.data.media_access, error.response.data.detail);
        } else {
          this.roomRecordsError = 'Не удалось загрузить записи встречи. Возможно, записи еще не готовы.'
        }
      } finally {
        this.roomRecordsLoading = false;
      }
    },
    closeRoomRecordsDialog() {
      this.showRoomRecordsDialog = false;
      this.selectedAppointmentForRecords = null;
      this.roomRecords = [];
      this.roomRecordsError = null;
      this.resetMediaAccessNotice();
      this.closeVideoPlayer(); // Закрываем плеер при закрытии диалога
      this.closeChatPanel(); // Закрываем чат при закрытии диалога
    },
    async checkVideoAndUpdateStatus(appointment) {
      if (!appointment || !appointment.id) return;
      
      // Set loading state for this specific appointment
      this.checkingVideoStatus[appointment.id] = true;
      
      try {
        if (!this.hasAppointmentMedia(appointment)) {
          this.showNotification('Видеокомната не найдена для этого приема', 'error');
          return;
        }
        
        // Fetch video records for this appointment
        const response = await staffApi.getAppointmentMediaRecords(appointment.id);
        
        // Check if video recordings exist
        if (response.data && response.data.result === 'success' && response.data.data) {
          const recordUrls = response.data.data.record_urls || [];
          
          if (recordUrls.length > 0) {
            // Video recordings exist, update appointment status to completed
            try {
              await staffApi.updateStaffAppointmentStatus(appointment.id, 'completed');
              
              // Update local appointment status
              const index = this.appointments.findIndex(a => a.id === appointment.id);
              if (index !== -1) {
                this.appointments[index].status = 'completed';
              }
              
              this.showNotification('Статус приема обновлен на "Завершено"', 'success');
              
              // Синхронизируем video_record и chat_history в МИС в фоне (не блокируем UI)
              staffApi.syncCompletionToMIS(appointment.id).catch(misError => {
                console.warn('MIS completion sync failed:', misError);
              });
            } catch (updateError) {
              console.error('Error updating appointment status:', updateError);
              this.showNotification('Ошибка при обновлении статуса приема', 'error');
            }
          } else {
            // No video recordings found
            this.showNotification('Видеозаписи для этого приема не найдены', 'warning');
          }
        } else {
          this.showNotification('Не удалось получить информацию о видеозаписях', 'error');
        }
      } catch (error) {
        console.error('Error checking video records:', error);
        this.showNotification('Ошибка при проверке наличия видеозаписи', 'error');
      } finally {
        // Remove loading state
        this.checkingVideoStatus[appointment.id] = false;
      }
    },
    async fetchAllAppointmentsForStats() {
      try {
        // Используем staff API для получения всех приемов врача
        const response = await staffApi.getStaffAppointments();
        
        // Handle both paginated and non-paginated responses
        let appointmentsData = [];
        if (response.data && Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          appointmentsData = response.data.results;
        } else {
          appointmentsData = [];
        }
        
        this.allAppointments = appointmentsData;
        this.allSlotsData = {};
        await this.fetchAllAppointmentDetails();
      } catch (error) {
        this.allAppointments = []; // Убеждаемся что это массив даже при ошибке
      }
    },
    async fetchAllAppointmentDetails() {
      // Build slot data from appointments (slots are now included in appointment response)
      this.allSlotsData = {};
      
      // Ensure allAppointments is an array
      if (!Array.isArray(this.allAppointments)) {
        this.allAppointments = [];
        return;
      }
      
      // Extract slot data from appointments
      this.allAppointments.forEach(appointment => {
        if (appointment.slot && typeof appointment.slot === 'object') {
          this.allSlotsData[appointment.slot.id] = appointment.slot;
        }
      });
      
      // Fetch patient data
      const patientIds = this.allAppointments.map(a => a.patient).filter(id => id);
      
      if (patientIds.length > 0) {
        try {
          for (const patientId of patientIds) {
            try {
              const response = await api.getPatientById(patientId);
              if (response.data) {
                this.patientsData[patientId] = response.data;
              }
            } catch (patientError) {
              // Create a placeholder
              this.patientsData[patientId] = { id: patientId };
            }
          }
        } catch (error) {
      // Error handled silently
        }
      }
    },
    
    async fetchVisitReasons() {
      try {
        const response = await api.getVisitReasons();
        if (response.data && Array.isArray(response.data)) {
          // Создаем объект для быстрого поиска по ID
          this.visitReasonsData = {};
          response.data.forEach(reason => {
            this.visitReasonsData[reason.id] = reason;
          });
        }
      } catch (error) {
      // Error handled silently
      }
    },
    
    getLocalizedVisitReasonName(reason) {
      if (!reason) return '';
      
      // Получаем текущий язык интерфейса
      const currentLocale = this.$i18n.locale;
      
      // Если текущий язык казахский и есть казахское название, используем его
      if (currentLocale === 'kk' && reason.name_kk) {
        return reason.name_kk;
      }
      
      // Во всех остальных случаях используем русское название
      return reason.name || reason.name_ru || 'Неизвестная причина';
    },
    
    getVisitReasonText(reasonId) {
      if (!reasonId) return '';
      
      // Если это уже текст, возвращаем как есть
      if (typeof reasonId === 'string' && isNaN(reasonId)) {
        return reasonId;
      }
      
      // Если это ID, ищем в справочнике
      const reason = this.visitReasonsData[reasonId];
      if (reason) {
        // Возвращаем локализованное название
        return this.getLocalizedVisitReasonName(reason);
      }
      return reasonId;
    },
    getPatientFullName(patient) {
      if (!patient) return 'Неизвестный пациент';
      
      // Используем full_name из API если есть
      if (patient.full_name) {
        return patient.full_name;
      }
      
      // Пробуем получить из user объекта (детальная информация)
      if (patient.user) {
        const parts = [];
        if (patient.user.first_name) parts.push(patient.user.first_name);
        if (patient.user.middle_name) parts.push(patient.user.middle_name);
        if (patient.user.last_name) parts.push(patient.user.last_name);
        return parts.join(' ') || 'Пациент';
      }
      
      // Фоллбэк на прямые поля (если есть)
      const fullName = [];
      if (patient.first_name) fullName.push(patient.first_name);
      if (patient.middle_name) fullName.push(patient.middle_name);
      if (patient.last_name) fullName.push(patient.last_name);
      
      return fullName.join(' ') || 'Неизвестный пациент';
    },
    goToPatients() {
      // Переход на страницу пациентов
      this.$router.push('/patients')
    },
    goToCallsHistory() {
      // Переход на страницу истории звонков медсестер
      this.$router.push('/nurse-calls-history')
    },
    async createVideoCall(patient) {
      if (!confirm(`Создать видеозвонок с пациентом ${this.getPatientFullName(patient)}?`)) {
        return
      }
      
      try {
        this.patientsLoading = true
        const response = await api.createNurseVideoCall(patient.id)
        
        
        // Проверяем сначала video_room_url, потом room_url в качестве fallback
        const roomUrl = response.data.video_room_url || response.data.room_url
        
        if (roomUrl) {
          // Открываем видеозвонок в новом окне
          const { openVideoRoomWithLanguage } = await import('@/utils/videoRoom');
          openVideoRoomWithLanguage(roomUrl, this.$i18n.locale);
          
                  // Показываем диалог с возможностью поделиться ссылкой
        setTimeout(() => {
          const shareConfirm = confirm(this.$t('appointment.videoCallCreatedSharePrompt'));
          if (shareConfirm) {
            this.copyRoomLinkToClipboard(roomUrl);
          }
        }, 1000);
        } else {
          alert(`Видеозвонок создан, но ссылка недоступна. Ответ: ${JSON.stringify(response.data)}`)
        }
      } catch (error) {
        // Показываем более детальное сообщение об ошибке
        if (error.response && error.response.status === 503) {
          alert('Сервис видеосвязи временно недоступен. Попробуйте позже.')
        } else if (error.message && error.message.includes('видеокомнату')) {
          alert(error.message)
        } else {
          alert('Не удалось создать видеозвонок. Попробуйте позже.')
        }
      } finally {
        this.patientsLoading = false
      }
    },
    goToPatientDetails(patient) {
      this.$router.push(`/patients/${patient.id}`)
    },
    
    async copyRoomLinkToClipboard(roomUrl) {
      try {
        // Добавляем языковой параметр к URL
        const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
        const urlWithLanguage = addLanguageToVideoRoomUrl(roomUrl, this.$i18n.locale);
        
        // Копируем ссылку в буфер обмена
        await navigator.clipboard.writeText(urlWithLanguage);
        
        // Показываем уведомление об успешном копировании
        this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        
      } catch (error) {
        
        // Fallback для браузеров без поддержки clipboard API
        try {
          const { addLanguageToVideoRoomUrl } = await import('@/utils/videoRoom');
          const urlWithLanguage = addLanguageToVideoRoomUrl(roomUrl, this.$i18n.locale);
          
          const textArea = document.createElement('textarea');
          textArea.value = urlWithLanguage;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          this.showNotification(this.$t('appointment.roomLinkCopied'), 'success');
        } catch (fallbackError) {
          alert('Не удалось скопировать ссылку автоматически. Ссылка на комнату: ' + roomUrl);
        }
      }
    },
    playVideo(videoUrl) {
      this.selectedVideoUrl = videoUrl;
      this.showVideoPlayer = true;
    },
    closeVideoPlayer() {
      this.selectedVideoUrl = null;
      this.showVideoPlayer = false;
    },
    onVideoLoaded() {
      // No-op hook for future video metadata handling
    },
    // Автоматическое обновление приемов
    startAutoRefresh() {
      // Обновляем каждые 4 минуты (было 30 секунд - слишком часто)
      this.autoRefreshInterval = setInterval(() => {
        this.refreshAppointmentsQuietly();
      }, 240000); // 4 минуты
    },
    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },
    async refreshAppointmentsQuietly() {
      try {
        // Не обновляем данные, если пользователь активно работает с модальными окнами
        if (this.showConclusionModal || this.showConsiliumDialog || this.showCancellationDialog || this.showRoomRecordsDialog) {
          return;
        }
        
        // Сохраняем текущее количество приемов
        const currentCount = this.appointments.length;
        
        // Загружаем обновленные данные без индикаторов загрузки
        // Используем staff API с правильными эндпоинтами
        let response;
        if (this.activeAppointmentsTab === 'upcoming') {
          response = await staffApi.getStaffUpcomingAppointments();
        } else if (this.activeAppointmentsTab === 'today') {
          response = await staffApi.getStaffTodayAppointments();
        } else if (this.activeAppointmentsTab === 'past') {
          response = await staffApi.getStaffPastAppointments();
        } else {
          const params = { status: this.activeAppointmentsTab };
          response = await staffApi.getStaffAppointments(params);
        }
        
        let appointmentsData = [];
        if (response.data && Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          appointmentsData = response.data.results;
        }
        
        // Если количество приемов изменилось, обновляем данные
        if (appointmentsData.length !== currentCount) {
          this.appointments = appointmentsData;
          await this.fetchAppointmentDetails();
          
          // Показываем уведомление о новых приемах
          if (appointmentsData.length > currentCount) {
            const newCount = appointmentsData.length - currentCount;
            this.showNotification(`Добавлено новых приемов: ${newCount}`, 'success');
          }
          
        }
        
        // Также обновляем статистику
        await this.fetchAllAppointmentsForStats();
      } catch (error) {
        // Не показываем ошибку пользователю при автоматическом обновлении
      }
    },
    showNotification(message, type = 'info') {
      // Создаем красивое toast-уведомление
      const toast = {
        id: Date.now() + Math.random(),
        title: type === 'success' ? this.$t('messages.successTitle') : (type === 'error' ? this.$t('messages.errorTitle') : this.$t('messages.infoTitle')),
        message: message,
        type: type,
        show: false
      };
      
      this.toasts.push(toast);
      
      // Показываем уведомление с небольшой задержкой для анимации
      setTimeout(() => {
        toast.show = true;
      }, 100);
      
      // Автоматически убираем уведомление через 5 секунд
      setTimeout(() => {
        this.removeToast(toast.id);
      }, 30000);
      
      // Логирование не требуется
    },
    removeToast(id) {
      const toastIndex = this.toasts.findIndex(toast => toast.id === id);
      if (toastIndex > -1) {
        // Сначала скрываем с анимацией
        this.toasts[toastIndex].show = false;
        
        // Затем удаляем из массива
        setTimeout(() => {
          this.toasts = this.toasts.filter(toast => toast.id !== id);
        }, 300);
      }
    },
    calculateAge(birthDate) {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    },
    
    // Appointment conclusion methods
    resetConclusionForm() {
      this.conclusionForm = {
        complaints: '',
        disease_history: '',
        life_anamnesis: '',
        allergy_anamnesis: '',
        epidemiological_anamnesis: '',
        transfusion_anamnesis: '',
        general_condition: '',
        d_registration: '',
        d_registration_details: '',
        tbs: '',
        tbs_details: '',
        allergies: '',
        allergies_details: '',
        viral_hepatitis: '',
        viral_hepatitis_details: '',
        temperature: '',
        blood_pressure: '',
        pulse: '',
        respiratory_rate: '',
        edema: '',
        edema_details: '',
        skin_condition: '',
        skin_condition_details: '',
        urination: '',
        stool: '',
        genital_discharge: '',
        genital_discharge_details: '',
        examinations: '',
        recommendations: '',
        final_protocol: ''
      };
    },
    
    // Метод для автоматической генерации финального протокола
    generateFinalProtocol() {
      const form = this.conclusionForm;
      let protocol = [];
      
      // Жалобы
      if (form.complaints) {
        protocol.push("ЖАЛОБЫ:");
        protocol.push(form.complaints);
        protocol.push("");
      }
      
      // Анамнез заболевания
      if (form.disease_history) {
        protocol.push("АНАМНЕЗ ЗАБОЛЕВАНИЯ:");
        protocol.push(form.disease_history);
        protocol.push("");
      }
      
      // Анамнез жизни
      let anamnesisLife = [];
      if (form.d_registration === 'true') {
        anamnesisLife.push(`Состоит на "Д" учете: Да${form.d_registration_details ? ' (' + form.d_registration_details + ')' : ''}`);
      }
      if (form.tbs === 'true') {
        anamnesisLife.push(`TBS: Да${form.tbs_details ? ' (' + form.tbs_details + ')' : ''}`);
      }
      if (form.allergies === 'true') {
        anamnesisLife.push(`Аллергоанамнез: Да${form.allergies_details ? ' (' + form.allergies_details + ')' : ''}`);
      }
      if (form.viral_hepatitis === 'true') {
        anamnesisLife.push(`Вирусный гепатит: Да${form.viral_hepatitis_details ? ' (' + form.viral_hepatitis_details + ')' : ''}`);
      }
      
      if (anamnesisLife.length > 0) {
        protocol.push("АНАМНЕЗ ЖИЗНИ:");
        protocol.push(...anamnesisLife);
        protocol.push("");
      }
      
      // Объективные данные
      let objectiveData = [];
      if (form.temperature) objectiveData.push(`${this.$t('medical.conclusion.temperatureLabel')}: ${form.temperature}°C`);
      if (form.blood_pressure) objectiveData.push(`${this.$t('medical.conclusion.bloodPressureLabel')}: ${form.blood_pressure} мм.рт.ст.`);
      if (form.pulse) objectiveData.push(`${this.$t('medical.conclusion.pulseLabel')}: ${form.pulse} уд/мин`);
      if (form.respiratory_rate) objectiveData.push(`${this.$t('medical.conclusion.respiratoryRateLabel')}: ${form.respiratory_rate} в мин`);
      
      if (form.edema === 'true') {
        objectiveData.push(`${this.$t('medical.conclusion.edema')}: ${this.$t('medical.conclusion.yes')}${form.edema_details ? ' (' + form.edema_details + ')' : ''}`);
      }
      if (form.skin_condition === 'true') {
        objectiveData.push(`${this.$t('medical.conclusion.skinCondition')}: Изменения${form.skin_condition_details ? ' (' + form.skin_condition_details + ')' : ''}`);
      }
      if (form.urination) objectiveData.push(`${this.$t('medical.conclusion.urination')}: ${form.urination}`);
      if (form.stool) objectiveData.push(`${this.$t('medical.conclusion.stool')}: ${form.stool}`);
      if (form.genital_discharge === 'true') {
        objectiveData.push(`${this.$t('medical.conclusion.genitalDischarge')}: ${this.$t('medical.conclusion.yes')}${form.genital_discharge_details ? ' (' + form.genital_discharge_details + ')' : ''}`);
      }
      
      if (objectiveData.length > 0) {
        protocol.push(this.$t('medical.conclusion.physicalData').toUpperCase() + ":");
        protocol.push(...objectiveData);
        protocol.push("");
      }
      
      // Назначения
      if (form.examinations) {
        protocol.push(this.$t('medical.conclusion.examinations').toUpperCase() + ":");
        protocol.push(form.examinations);
        protocol.push("");
      }
      
      // Рекомендации
      if (form.recommendations) {
        protocol.push(this.$t('medical.conclusion.recommendations').toUpperCase() + ":");
        protocol.push(form.recommendations);
        protocol.push("");
      }
      
      return protocol.join('\n');
    },
    
    // Следим за изменениями в форме и генерируем протокол
    updateFinalProtocol() {
      this.conclusionForm.final_protocol = this.generateFinalProtocol();
    },
    
    loadConclusionToForm(conclusionData) {
      if (!conclusionData) return;
      
      // Загружаем данные заключения в форму
      this.conclusionForm = {
        complaints: conclusionData.complaints || '',
        disease_history: conclusionData.disease_history || '',
        life_anamnesis: conclusionData.life_anamnesis || '',
        allergy_anamnesis: conclusionData.allergy_anamnesis || '',
        epidemiological_anamnesis: conclusionData.epidemiological_anamnesis || '',
        transfusion_anamnesis: conclusionData.transfusion_anamnesis || '',
        general_condition: conclusionData.general_condition || '',
        d_registration: conclusionData.d_registration || '',
        d_registration_details: conclusionData.d_registration_details || '',
        tbs: conclusionData.tbs || '',
        tbs_details: conclusionData.tbs_details || '',
        allergies: conclusionData.allergies || '',
        allergies_details: conclusionData.allergies_details || '',
        viral_hepatitis: conclusionData.viral_hepatitis || '',
        viral_hepatitis_details: conclusionData.viral_hepatitis_details || '',
        temperature: conclusionData.temperature || '',
        blood_pressure: conclusionData.blood_pressure || '',
        pulse: conclusionData.pulse || '',
        respiratory_rate: conclusionData.respiratory_rate || '',
        edema: conclusionData.edema || '',
        edema_details: conclusionData.edema_details || '',
        skin_condition: conclusionData.skin_condition || '',
        skin_condition_details: conclusionData.skin_condition_details || '',
        urination: conclusionData.urination || '',
        stool: conclusionData.stool || '',
        genital_discharge: conclusionData.genital_discharge || '',
        genital_discharge_details: conclusionData.genital_discharge_details || '',
        conclusion_raw: conclusionData.conclusion_raw || '',
        examinations: conclusionData.examinations || '',
        recommendations: conclusionData.recommendations || '',
        final_protocol: conclusionData.final_protocol || ''
      };
      
      // Если финальный протокол пустой, генерируем его
      if (!this.conclusionForm.final_protocol) {
        this.updateFinalProtocol();
      }
    },
    
    getConclusionButtonText() {
      if (!this.selectedAppointmentForConclusion) return this.$t('medical.conclusion.saveConclusion');
      
      // Если прием уже завершен (любой финальный статус), значит мы редактируем существующее заключение
      const completedStatuses = ['completed', 'patient_no_show', 'no_show']
      if (completedStatuses.includes(this.selectedAppointmentForConclusion.status)) {
        return this.$t('medical.conclusion.saveConclusion');
      }
      
      // Если прием в процессе, значит мы завершаем его
      return this.$t('appointments.completeAppointment');
    },
    
    closeConclusionModal() {
      this.showConclusionModal = false;
      // НЕ удаляем conclusion_id - он нужен для определения существующих заключений
      this.selectedAppointmentForConclusion = null;
      this.resetConclusionForm();
      this.showVideoWarningTooltip = false;
    },
    
    handleSaveAppointmentConclusion() {
      // If video is active, show tooltip and prevent save
      if (this.isVideoActive) {
        this.showVideoWarningTooltip = true;
        // Auto hide tooltip after 5 seconds
        setTimeout(() => {
          this.showVideoWarningTooltip = false;
        }, 5000);
        return;
      }
      
      // Otherwise proceed with save
      this.saveAppointmentConclusion();
    },
    
    async saveAppointmentConclusion() {
      if (!this.selectedAppointmentForConclusion) return;
      
      try {
        this.conclusionLoading = true;
        
        // Генерируем финальный протокол перед сохранением
        this.updateFinalProtocol();
        
        // Validate form data before sending
        if (!this.validateConclusionForm()) {
          return;
        }
        
        // Prepare the conclusion data and convert string booleans to actual booleans
        const conclusionData = {
          appointment_id: this.selectedAppointmentForConclusion.id, // Backend ожидает appointment_id для /conclusion/ endpoint
          ...this.conclusionForm,
          // Ensure choice fields have valid values or empty string
          urination: this.conclusionForm.urination || '',
          stool: this.conclusionForm.stool || '',
          // Convert string booleans to actual booleans (handle both string and boolean values)
          d_registration: this.conclusionForm.d_registration === 'true' || this.conclusionForm.d_registration === true ? true : 
                         this.conclusionForm.d_registration === 'false' || this.conclusionForm.d_registration === false ? false : null,
          tbs: this.conclusionForm.tbs === 'true' || this.conclusionForm.tbs === true ? true : 
               this.conclusionForm.tbs === 'false' || this.conclusionForm.tbs === false ? false : null,
          allergies: this.conclusionForm.allergies === 'true' || this.conclusionForm.allergies === true ? true : 
                     this.conclusionForm.allergies === 'false' || this.conclusionForm.allergies === false ? false : null,
          viral_hepatitis: this.conclusionForm.viral_hepatitis === 'true' || this.conclusionForm.viral_hepatitis === true ? true : 
                          this.conclusionForm.viral_hepatitis === 'false' || this.conclusionForm.viral_hepatitis === false ? false : null,
          edema: this.conclusionForm.edema === 'true' || this.conclusionForm.edema === true ? true : 
                 this.conclusionForm.edema === 'false' || this.conclusionForm.edema === false ? false : null,
          skin_condition: this.conclusionForm.skin_condition === 'true' || this.conclusionForm.skin_condition === true ? true : 
                         this.conclusionForm.skin_condition === 'false' || this.conclusionForm.skin_condition === false ? false : null,
          genital_discharge: this.conclusionForm.genital_discharge === 'true' || this.conclusionForm.genital_discharge === true ? true : 
                            this.conclusionForm.genital_discharge === 'false' || this.conclusionForm.genital_discharge === false ? false : null,
        };
        
        // Backend автоматически обрабатывает создание и обновление заключений
        // через один endpoint - всегда используем saveAppointmentConclusion
        await api.saveAppointmentConclusion(conclusionData);
        
        // Проверяем, был ли прием уже в финальном статусе
        const completedStatuses = ['completed', 'patient_no_show', 'no_show']
        const isNewCompletion = !completedStatuses.includes(this.selectedAppointmentForConclusion.status);
        
        // Note: appointment status is automatically updated by the backend based on video room and records
        // НЕ устанавливаем статус вручную - получим актуальный статус с сервера при перезагрузке
        
        // Показываем красивое сообщение об успехе
        const message = isNewCompletion ? 
          'Прием успешно завершен и заключение сохранено' : 
          'Заключение успешно обновлено';
        this.showNotification(message, 'success');
        
        // Закрываем модальное окно с небольшой задержкой
        setTimeout(() => {
          this.closeConclusionModal();
        }, 800); // 0.8 секунды для показа уведомления
        
        // Всегда обновляем список приемов для получения актуального статуса с сервера
        setTimeout(() => {
          this.fetchAppointments();
        }, 500);
        
        // Опционально обновляем статистику в фоне (без ожидания)
        // Только если это новое завершение приема, которое может повлиять на статистику
        if (isNewCompletion) {
          setTimeout(() => {
            this.fetchAllAppointmentsForStats();
          }, 1000); // Обновляем статистику через 1 секунду в фоне
        }
      } catch (error) {
        
        // Более детальная обработка ошибок
        let errorMessage = 'Произошла ошибка при сохранении заключения приема';
        
        if (error.response) {
          // Показываем детальную ошибку от сервера если есть
          if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data && typeof error.response.data === 'object') {
            // Если ошибка в виде объекта с полями валидации
            const errorDetails = [];
            for (const [field, messages] of Object.entries(error.response.data)) {
              if (Array.isArray(messages)) {
                errorDetails.push(`${field}: ${messages.join(', ')}`);
              } else {
                errorDetails.push(`${field}: ${messages}`);
              }
            }
            if (errorDetails.length > 0) {
              errorMessage = `Ошибки валидации:\n${errorDetails.join('\n')}`;
            }
          } else if (error.response.status === 400) {
            errorMessage = 'Некорректные данные заключения. Проверьте консоль для деталей.';
          } else if (error.response.status === 404) {
            errorMessage = 'Прием не найден';
          } else if (error.response.status === 403) {
            errorMessage = 'Недостаточно прав для сохранения заключения';
          }
        }
        
        this.showNotification(errorMessage, 'error');
      } finally {
        this.conclusionLoading = false;
      }
    },
    
    validateConclusionForm() {
      // Проверяем обязательные поля (можно добавить больше проверок по необходимости)
      if (!this.conclusionForm.complaints && !this.conclusionForm.disease_history && 
          !this.conclusionForm.examinations && !this.conclusionForm.recommendations) {
        this.showNotification('Заполните хотя бы одно из основных полей заключения', 'error');
        return false;
      }
      
      // Проверяем корректность числовых значений
      if (this.conclusionForm.temperature && !/^\d+([.,]\d+)?$/.test(this.conclusionForm.temperature.replace('°C', ''))) {
        this.showNotification('Некорректное значение температуры', 'error');
        return false;
      }
      
      if (this.conclusionForm.blood_pressure && !/^\d+\/\d+$/.test(this.conclusionForm.blood_pressure)) {
        this.showNotification('Артериальное давление должно быть в формате "120/80"', 'error');
        return false;
      }
      
      if (this.conclusionForm.pulse && !/^\d+/.test(this.conclusionForm.pulse)) {
        this.showNotification('Некорректное значение пульса', 'error');
        return false;
      }
      
      if (this.conclusionForm.respiratory_rate && !/^\d+/.test(this.conclusionForm.respiratory_rate)) {
        this.showNotification('Некорректное значение частоты дыхания', 'error');
        return false;
      }
      
      return true;
    },
    
    copyProtocol() {
      if (this.conclusionForm.final_protocol) {
        navigator.clipboard.writeText(this.conclusionForm.final_protocol).then(() => {
          this.showNotification('Протокол скопирован в буфер обмена', 'success');
        }).catch(() => {
          this.showNotification('Ошибка копирования в буфер обмена', 'error');
        });
      } else {
        this.showNotification('Нет данных для копирования', 'error');
      }
    },
    
    // Chat methods
    async loadRoomChat(appointmentId) {
      this.roomChatLoading = true
      this.roomChatError = null
      this.roomChatMessages = []
      
      try {
        const response = await staffApi.getAppointmentMediaChat(appointmentId)
        
        if (response.data && response.data.messages) {
          this.roomChatMessages = response.data.messages || []
        } else if (response.data && response.data.result === 'error') {
          this.roomChatError = response.data.error || 'Ошибка получения чата'
        } else {
          this.roomChatError = this.$t('messages.chatNotEnabled')
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.media_access) {
          this.closeChatPanel()
          this.setMediaAccessNotice(error.response.data.media_access, error.response.data.detail)
        } else {
          this.roomChatError = 'Не удалось загрузить сообщения чата'
        }
      } finally {
        this.roomChatLoading = false
      }
    },
    showChatForRoom() {
      if (!this.selectedAppointmentForRecords) return

      if (this.mediaAccessNotice.visible) {
        return
      }

      if (!this.selectedAppointmentForRecords.id) {
        this.roomChatError = 'Некорректный прием'
        return
      }
      
      if (this.showChatPanel) {
        // Если чат уже открыт, закрываем его
        this.closeChatPanel()
      } else {
        // Если чат закрыт, открываем и загружаем сообщения
        this.showChatPanel = true
        this.loadRoomChat(this.selectedAppointmentForRecords.id)
      }
    },
    closeChatPanel() {
      this.showChatPanel = false
      this.roomChatMessages = []
      this.roomChatError = null
    },
    resetMediaAccessNotice() {
      this.mediaAccessNotice = {
        visible: false,
        status: null,
        title: '',
        description: '',
        canRequest: false,
        requestSending: false
      }
    },
    setMediaAccessNotice(mediaAccess, detail = '') {
      const status = mediaAccess?.status || 'not_requested'
      let title = 'Нужно разрешение пациента'
      let description = 'В этом приеме есть видеозапись и чат. Для просмотра отправьте запрос пациенту.'
      let canRequest = Boolean(mediaAccess?.can_request_permission)

      if (status === 'no_media') {
        title = 'Запись недоступна'
        description = 'Для этого приема пока нет видеозаписи и истории чата.'
        canRequest = false
      } else if (status === 'pending') {
        title = 'Запрос уже отправлен'
        description = 'Пациент еще не принял решение. Как только доступ будет одобрен, вы сможете открыть видео и чат.'
        canRequest = false
      } else if (status === 'rejected') {
        title = 'Запрос был отклонен'
        description = 'Пациент отклонил предыдущий запрос. При необходимости можно отправить запрос повторно.'
        canRequest = Boolean(mediaAccess?.can_request_permission)
      } else if (status === 'forbidden' && detail) {
        title = 'Доступ ограничен'
        description = detail
        canRequest = false
      }

      this.mediaAccessNotice = {
        visible: true,
        status,
        title,
        description,
        canRequest,
        requestSending: false
      }
    },
    async requestMediaAccessForSelectedAppointment() {
      if (!this.selectedAppointmentForRecords || !this.selectedAppointmentForRecords.id) return

      this.mediaAccessNotice = {
        ...this.mediaAccessNotice,
        requestSending: true
      }

      try {
        const response = await staffApi.requestAppointmentMediaAccess(this.selectedAppointmentForRecords.id)
        const mediaAccess = response.data?.media_access || { status: 'pending', can_request_permission: false }
        this.setMediaAccessNotice(mediaAccess)
      } catch (error) {
        this.mediaAccessNotice = {
          ...this.mediaAccessNotice,
          requestSending: false,
          description: 'Не удалось отправить запрос. Проверьте соединение и попробуйте еще раз.'
        }
      }
    },
    formatChatTimestamp(timestamp) {
      if (!timestamp) return ''
      
      // Обрабатываем и ISO строку (created_at) и unix timestamp (timestamp)
      let date
      if (typeof timestamp === 'string') {
        date = new Date(timestamp)
      } else if (typeof timestamp === 'number') {
        // Unix timestamp в секундах, конвертируем в миллисекунды
        date = new Date(timestamp * 1000)
      } else {
        return ''
      }
      
      return date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      })
    },
    hasAppointmentMedia(appointment) {
      return Boolean(appointment && (appointment.has_media_content || appointment.video_room_name))
    },
    getMessageText(message) {
      try {
        // Если есть прямое поле text (из mock данных)
        if (message.text) {
          return message.text;
        }
        
        // Парсим JSON из поля message
        if (message.message) {
          const parsedMessage = JSON.parse(message.message);
          return parsedMessage.text || null;
        }
      } catch (error) {
        // Failed to parse message JSON
        // Возвращаем сырое сообщение если парсинг не удался
        return message.message || null;
      }
      return null;
    },
    getMessageFiles(message) {
      try {
        // Если есть прямое поле files (из mock данных)
        if (message.files) {
          return message.files;
        }
        
        // Парсим JSON из поля message
        if (message.message) {
          const parsedMessage = JSON.parse(message.message);
          return parsedMessage.files || [];
        }
      } catch (error) {
        // Failed to parse message JSON for files
        return [];
      }
      return [];
    },
    openImageModal(url, alt) {
      this.modalImageUrl = url
      this.modalImageAlt = alt
      this.showImageModal = true
    },
    closeImageModal() {
      this.showImageModal = false
      this.modalImageUrl = null
      this.modalImageAlt = null
    },
    formatFileSize(size) {
      if (size) {
        if (size < 1024) {
          return size + ' байт';
        } else if (size < 1024 * 1024) {
          return (size / 1024).toFixed(2) + ' КБ';
        } else {
          return (size / (1024 * 1024)).toFixed(2) + ' МБ';
        }
      }
      return 'Неизвестный размер';
    },
    
    // Conclusion methods
    async showAppointmentConclusion(appointment) {
      // Заключение доступно для приемов со статусами: completed, patient_no_show, no_show
      const allowedStatuses = ['completed', 'patient_no_show', 'no_show']
      if (!appointment || !allowedStatuses.includes(appointment.status)) {
        this.showNotification('Заключение доступно только для завершенных приемов', 'error');
        return;
      }
      
      try {
        this.conclusionLoading = true;
        
        // Устанавливаем прием для редактирования
        this.selectedAppointmentForConclusion = appointment;
        this.resetConclusionForm();
        
        // Сначала проверяем локально сохраненные данные
        if (appointment.conclusion_data) {
          this.loadConclusionToForm(appointment.conclusion_data);
          // Сохраняем ID заключения для возможности редактирования
          this.selectedAppointmentForConclusion.conclusion_id = appointment.conclusion_data.id;
        } else {
          // Принудительная загрузка с сервера с добавлением timestamp для обхода кэша
          const timestamp = new Date().getTime();
          const response = await api.getAppointmentConclusion(appointment.id, { t: timestamp });
        
        if (response.data) {
            this.loadConclusionToForm(response.data);
            
            // Сохраняем ID заключения для возможности редактирования
            this.selectedAppointmentForConclusion.conclusion_id = response.data.id;
            
            // Кэшируем данные локально для последующих просмотров
            const appointmentIndex = this.appointments.findIndex(a => a.id === appointment.id);
            if (appointmentIndex !== -1) {
              this.appointments[appointmentIndex].conclusion_data = response.data;
            }
        }
        }
        
        // Открываем модальное окно редактирования
        this.showConclusionModal = true;
        
      } catch (error) {
        
        // Обработка различных типов ошибок
        if (error.response && error.response.status === 404) {
          // Заключение не найдено, открываем пустую форму для создания
          this.showConclusionModal = true;
        } else if (error.message === 'Заключение еще не создано для данного приема') {
          this.showConclusionModal = true;
        } else {
          this.showNotification(this.$t('messages.conclusionLoadError'), 'error');
        }
      } finally {
        this.conclusionLoading = false;
      }
    },
    onImageError(event) {
      // Скрываем изображение если оно не загрузилось
      event.target.style.display = 'none'
    },

    // Helper method to get video room URL from appointment
    getVideoRoomUrl(appointment) {
      return appointment.video_room_url || null;
    },

    // Helper method to check if video room is available
    hasVideoRoom(appointment) {
      return !!(appointment.video_room_url && 
               (appointment.status === 'scheduled' || 
                appointment.status === 'in_progress'));
    },


  }
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors;
}

.btn-secondary {
  @apply px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors;
}

/* Современные стили для input date */
.modern-date-input {
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  letter-spacing: 0.025em;
  color: #374151;
  background-color: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  min-width: 140px;
}

.modern-date-input:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.modern-date-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  background-color: #fefefe;
}

/* Стили для календарного виджета Chrome/Safari */
.modern-date-input::-webkit-calendar-picker-indicator {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%233b82f6'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3E%3C/svg%3E");
  background-size: 16px 16px;
  background-repeat: no-repeat;
  background-position: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: 4px;
}

.modern-date-input::-webkit-calendar-picker-indicator:hover {
  background-color: rgba(59, 130, 246, 0.1);
}

/* Стили для Firefox */
.modern-date-input::-moz-calendar-picker-indicator {
  filter: invert(0.4) sepia(1) saturate(2) hue-rotate(200deg);
}

/* Стили для clear button в Chrome */
.modern-date-input::-webkit-clear-button {
  display: none;
}

/* Анимация появления фильтра */
@keyframes filterAppear {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.filter-indicator {
  animation: filterAppear 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Анимация появления tooltip снизу вверх */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out;
}

/* Улучшенные стили для date range компонента */
.date-range-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.date-range-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-color: rgba(59, 130, 246, 0.3);
}

/* Responsive стили */
@media (max-width: 768px) {
  .modern-date-input {
    min-width: 120px;
    font-size: 14px;
    padding: 8px 12px;
  }
  
  .date-range-container {
    padding: 12px;
  }
}
</style> 
